import SectionQuiz from '../components/HostTraining/shared/SectionQuiz';
import {sectionCheckFor, PRACTICE_VERSION} from '../components/HostTraining/shared/sectionChecks';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { FaInstagram, FaLinkedin } from 'react-icons/fa';
import { LuArrowRight, LuArrowLeft, LuCheck, LuPause, LuPlay, LuRotateCcw } from 'react-icons/lu';
import { ReactComponent as TribeLogo } from '../assets/TribeLogo.svg';

import Welcome from '../components/HostTraining/Background/Welcome';
import WhatIsTribe from '../components/HostTraining/Background/WhatIsTribe';
import EventStructure from '../components/HostTraining/Background/EventStructure';
import YourRole from '../components/HostTraining/Background/YourRole';
import EventOffer from '../components/HostTraining/PreparingForAnEvent/EventOffer';
import EventGuide from '../components/HostTraining/PreparingForAnEvent/EventGuide';
import ArrivalAndPortal from '../components/HostTraining/PreparingForAnEvent/ArrivalAndPortal';
import Conversation from '../components/HostTraining/Facilitation/Conversation';
import HelpGuestsConnect from '../components/HostTraining/Facilitation/HelpGuestsConnect';
import UsingYourAuthority from '../components/HostTraining/Facilitation/UsingYourAuthority';
import Safety from '../components/HostTraining/Facilitation/Safety';
import HostExpectations from '../components/HostTraining/Operations/HostExpectations';
import Payments from '../components/HostTraining/Operations/Payments';
import FinalQuiz from '../components/HostTraining/Operations/FinalQuiz';
import ClosingEvent from '../components/HostTraining/Facilitation/ClosingEvent';

const LESSONS = [
  Welcome,
  WhatIsTribe,
  EventStructure,
  YourRole,
  EventOffer,
  EventGuide,
  ArrivalAndPortal,
  Conversation,
  HelpGuestsConnect,
  UsingYourAuthority,
  Safety,
  ClosingEvent,
  HostExpectations,
  Payments,
  FinalQuiz,
].map((Component, index) => ({
  ...Component,
  slide: index + 1,
  Component,
}));

const COURSE_VERSION = "host-training-2026-10-v1";

const API = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
const EMAIL_KEY = 'tribeHostTrainingEmail';
// Browser narration cache and preloading are integrated into this file.
// The cache holds Blobs, not object URLs. Each player owns and revokes its URL.
const DATABASE = 'tribe-host-training-narration-v1';
const MAX_DISK_BYTES = 128 * 1024 * 1024;
const MAX_AGE = 7 * 24 * 60 * 60 * 1000;
let databasePromise;

function openDatabase() {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  if (!databasePromise) databasePromise = new Promise(resolve => {
    let settled = false;
    let request;
    const timer = window.setTimeout(() => finish(null), 1500);
    function finish(db) {
      if (settled) { if (db) db.close(); return; }
      settled = true; window.clearTimeout(timer); resolve(db);
    }
    try {
      request = indexedDB.open(DATABASE, 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore('audio', { keyPath: 'key' });
        request.result.createObjectStore('metadata', { keyPath: 'key' });
      };
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => { db.close(); databasePromise = undefined; };
        finish(db);
      };
      request.onerror = request.onblocked = () => finish(null);
    } catch { finish(null); }
  });
  return databasePromise;
}

async function readSaved(key) {
  const db = await openDatabase();
  if (!db) return null;
  return new Promise(resolve => {
    try {
      const request = db.transaction('audio', 'readonly').objectStore('audio').get(key);
      request.onsuccess = () => {
        const entry = request.result;
        resolve(entry && Date.now() - entry.savedAt < MAX_AGE ? entry.value : null);
      };
      request.onerror = () => resolve(null);
    } catch { resolve(null); }
  });
}

async function writeSaved(key, value) {
  const db = await openDatabase();
  if (!db) return;
  await new Promise(resolve => {
    try {
      const transaction = db.transaction(['audio', 'metadata'], 'readwrite');
      const audio = transaction.objectStore('audio');
      const metadata = transaction.objectStore('metadata');
      const savedAt = Date.now(), bytes = value.blob.size + JSON.stringify(value.words).length * 2;
      audio.put({ key, value, savedAt });
      metadata.put({ key, bytes, savedAt });
      const request = metadata.getAll();
      request.onsuccess = () => {
        const rows = request.result.sort((a, b) => a.savedAt - b.savedAt);
        let total = rows.reduce((sum, row) => sum + row.bytes, 0);
        for (const row of rows) {
          if (Date.now() - row.savedAt < MAX_AGE && total <= MAX_DISK_BYTES) break;
          audio.delete(row.key); metadata.delete(row.key); total -= row.bytes;
        }
      };
      transaction.oncomplete = transaction.onerror = transaction.onabort = () => resolve();
    } catch { resolve(); }
  });
}

function narrationCacheKey(target) {
  return JSON.stringify([target.api, target.email.trim().toLowerCase(), target.courseVersion,
    target.audioVersion || 'v1', target.slide, target.part, target.narration]);
}

async function decodeNarration(response) {
  let blob = response.data;
  if (!(blob instanceof Blob) || !blob.size) throw new Error('Narration returned no audio.');
  if (!blob.type.includes('json')) return { blob, words: [], transcript: '', duration: 0 };
  const payload = JSON.parse(await blob.text());
  if (typeof payload.audioBase64 !== 'string' || !payload.audioBase64 ||
      !Array.isArray(payload.words) || !payload.words.length) {
    throw new Error('Narration did not include word timestamps.');
  }
  const words = payload.words.map((word, index) => {
    if (typeof word.word !== 'string' || !Number.isFinite(word.start) || !Number.isFinite(word.end) ||
        word.start < 0 || word.end < word.start || (index > 0 && word.start < payload.words[index - 1].start)) {
      throw new Error('Narration returned invalid timestamps.');
    }
    return { ...word, index };
  });
  const binary = atob(payload.audioBase64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index);
  blob = new Blob([bytes], { type: payload.mimeType || 'audio/wav' });
  if (!blob.size) throw new Error('Narration returned no audio.');
  return { blob, words, transcript: payload.transcript || '', duration: payload.duration || 0 };
}

function createNarrationClient({ request, read = readSaved, write = writeSaved,
  maxMemoryBytes = 96 * 1024 * 1024 }) {
  const memory = new Map(), pending = new Map();
  let memoryBytes = 0;
  function remember(key, value) {
    if (memory.has(key)) memoryBytes -= memory.get(key).blob.size;
    memory.delete(key); memory.set(key, value); memoryBytes += value.blob.size;
    while (memoryBytes > maxMemoryBytes && memory.size > 1) {
      const oldest = memory.keys().next().value;
      memoryBytes -= memory.get(oldest).blob.size; memory.delete(oldest);
    }
  }
  function get(target) {
    const key = narrationCacheKey(target);
    if (memory.has(key)) {
      const value = memory.get(key); remember(key, value); return Promise.resolve(value);
    }
    if (pending.has(key)) return pending.get(key);
    const job = (async () => {
      let value;
      try { value = await read(key); } catch { /* Storage is optional. */ }
      if (!value || !(value.blob instanceof Blob) || !value.blob.size) {
        value = await decodeNarration(await request(target));
        // Playback must not wait for a disk write or quota cleanup.
        Promise.resolve().then(() => write(key, value)).catch(() => {});
      }
      remember(key, value);
      return value;
    })();
    pending.set(key, job);
    job.then(() => pending.delete(key), () => pending.delete(key));
    return job;
  }
  return {
    get,
    prefetch: target => get(target).then(() => true, () => false),
    forget: async target => {
      const key = narrationCacheKey(target);
      if (memory.has(key)) { memoryBytes -= memory.get(key).blob.size; memory.delete(key); }
      // A media decode failure should not restore the same saved Blob on retry.
      await openDatabase().then(db => {
        if (!db) return;
        return new Promise(resolve => {
          try {
            const transaction = db.transaction(['audio', 'metadata'], 'readwrite');
            transaction.objectStore('audio').delete(key);
            transaction.objectStore('metadata').delete(key);
            transaction.oncomplete = transaction.onerror = transaction.onabort = () => resolve();
          } catch { resolve(); }
        });
      });
    },
  };
}

const AUDIO_VERSION = process.env.REACT_APP_HOST_TRAINING_AUDIO_VERSION || 'v1';
const narrationClient = createNarrationClient({
  request: target => axios.post(`${target.api}/host-onboarding/training/narration`,
    {email:target.email, slide:target.slide, part:target.part, includeTimestamps:true},
    {responseType:'blob', timeout:150000}),
});
const audioTarget = (email, slide, part) => ({email, slide, part, api:API,
  courseVersion:COURSE_VERSION, audioVersion:AUDIO_VERSION,
  narration:LESSONS[slide - 1].parts[part].narration});

const clock = seconds => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;

function Button({ children, secondary = false, ...props }) {
  return <button type="button" className={`ht-button${secondary ? ' is-secondary' : ''}`} {...props}>{children}</button>;
}

function ConfirmQuiz({ training, loading, error, onCancel, onConfirm }) {
  const ref = useRef(null);
  useEffect(() => { const dialog = ref.current; dialog?.showModal(); return () => dialog?.close(); }, []);
  return <dialog ref={ref} className="ht-dialog" onCancel={event => { event.preventDefault(); if (!loading) onCancel(); }}>
    <h2>Ready for the final quiz?</h2>
    <p>You’ll answer 19 questions. This is attempt {Math.min((training?.quizAttempts || 0) + 1, 4)} of 4. Once you start, training review is locked until you submit the attempt.</p>
    {error && <p className="ht-error" role="alert">{error}</p>}
    <div className="ht-actions"><Button secondary onClick={onCancel} disabled={loading}>Keep reviewing</Button><Button onClick={onConfirm} disabled={loading}>{loading ? 'Starting…' : 'Start quiz'}</Button></div>
  </dialog>;
}

function Progress({ slide }) {
  return <div className="training-header-progress">
    <div className="training-progress-track" role="progressbar" aria-label="Current lesson position"
      aria-valuemin={0} aria-valuemax={LESSONS.length} aria-valuenow={slide}
      aria-valuetext={`Lesson ${slide} of ${LESSONS.length}: ${LESSONS[slide - 1].title}`}>
      <span className="training-progress-fill" style={{ width: `${slide / LESSONS.length * 100}%` }}/>
    </div>
    <div className="training-progress-labels">
      <span className="training-progress-title">{LESSONS[slide - 1].title}</span>
      <span className="training-progress-count">Lesson {slide} of {LESSONS.length}</span>
    </div>
  </div>;
}

function desktopAccess() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'allowed';
  const mobile = navigator.userAgentData?.mobile === true ||
    /Android|iPhone|iPad|iPod|Tablet|Mobile/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return mobile ? 'device' : 'allowed';
}

function readMobileNoticeViewport() {
  if (typeof window === 'undefined') return { height: 0, top: 0 };
  const viewport = window.visualViewport;
  const height = viewport?.height || window.innerHeight;
  return {
    height: Number.isFinite(height) && height > 0 ? Math.round(height) : 0,
    top: Math.max(0, Math.round(viewport?.offsetTop || 0)),
  };
}

async function copyCurrentTrainingLink() {
  if (!navigator.clipboard?.writeText) throw new Error('Clipboard is unavailable.');
  const link = window.location.href;
  // Call directly from the tap handler to preserve Safari's user activation.
  await navigator.clipboard.writeText(link);
  return link;
}

function DesktopBrowserIllustration() {
  return <svg className="ht-desktop-illustration" viewBox="0 0 280 184" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="ht-notice-metal" x1="140" y1="16" x2="140" y2="165" gradientUnits="userSpaceOnUse">
        <stop stopColor="#E8EAED"/><stop offset="1" stopColor="#D4D7DC"/>
      </linearGradient>
      <linearGradient id="ht-notice-base" x1="140" y1="162" x2="140" y2="170" gradientUnits="userSpaceOnUse">
        <stop stopColor="#F1F2F4"/><stop offset="1" stopColor="#C8CDD3"/>
      </linearGradient>
      <clipPath id="ht-notice-window-clip"><rect className="ht-notice-window-size" x="42" y="38" width="134" height="104" rx="6"/></clipPath>
    </defs>
    <ellipse cx="140" cy="173" rx="99" ry="4" fill="#111827" fillOpacity=".045"/>
    <rect x="20" y="14" width="240" height="151" rx="11" fill="url(#ht-notice-metal)" stroke="#B9BEC5"/>
    <rect x="25" y="19" width="230" height="141" rx="7" fill="#202327"/>
    <rect x="28" y="23" width="224" height="133" rx="4" fill="#F7F8FA"/>
    <circle cx="140" cy="17" r="1" fill="#8C929A"/>
    <rect className="ht-notice-window-size ht-notice-window-shadow" x="42" y="38" width="134" height="104" rx="6" fill="white" stroke="#D9DDE3" strokeWidth=".8"/>
    <g clipPath="url(#ht-notice-window-clip)">
      <rect x="42" y="38" width="196" height="21" fill="#F0F2F5"/>
      <path d="M42 59H238" stroke="#E1E4E9" strokeWidth=".7"/>
      <circle cx="51" cy="48" r="2" fill="#C6CBD2"/>
      <circle cx="58" cy="48" r="2" fill="#C6CBD2"/>
      <circle cx="65" cy="48" r="2" fill="#C6CBD2"/>
      <rect x="76" y="43" width="82" height="11" rx="3" fill="white" stroke="#E0E3E8" strokeWidth=".6"/>
      <text x="117" y="50.5" textAnchor="middle" fill="#969CA5" fontSize="5.5" fontFamily="Inter,Arial,sans-serif">jointribe.io</text>
      <rect x="56" y="76" width="55" height="5" rx="2.5" fill="#606872"/>
      <rect x="56" y="88" width="88" height="3" rx="1.5" fill="#D8DDE3"/>
      <rect x="56" y="97" width="66" height="3" rx="1.5" fill="#E3E6EB"/>
      <rect x="56" y="113" width="30" height="11" rx="5.5" fill="#69737F"/>
      <rect x="158" y="76" width="64" height="48" rx="5" fill="#F7F8FA" stroke="#E7EAF0"/>
      <rect x="168" y="88" width="39" height="3" rx="1.5" fill="#D8DDE3"/>
      <rect x="168" y="97" width="29" height="3" rx="1.5" fill="#E3E6EB"/>
      <rect x="168" y="111" width="19" height="3" rx="1.5" fill="#C8CFD8"/>
    </g>
    <g className="ht-notice-resize-cursor" stroke="#6C7581" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M-7 0H7M-7 0l3-3M-7 0l3 3M7 0l-3-3M7 0l-3 3"/>
    </g>
    <path d="M7 164h266l-8 4a17 17 0 0 1-8 2H23a17 17 0 0 1-8-2l-8-4Z" fill="url(#ht-notice-base)" stroke="#BDC3CB" strokeWidth=".8"/>
    <path d="M116 164h48l-2 2h-44l-2-2Z" fill="#BDC3CB"/>
  </svg>;
}

export default function HostTraining() {
  const [access, setAccess] = useState(desktopAccess);
  const [noticeViewport, setNoticeViewport] = useState(readMobileNoticeViewport);
  const [copyStatus, setCopyStatus] = useState('idle');
  const [manualCopyLink, setManualCopyLink] = useState('');
  const copyResetRef = useRef(null);
  useEffect(() => () => {
    if (copyResetRef.current !== null) window.clearTimeout(copyResetRef.current);
  }, []);
  useEffect(() => {
    const update = () => setAccess(desktopAccess());
    update(); window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  useEffect(() => {
    if (access !== 'device') return undefined;
    const viewport = window.visualViewport;
    let frame = 0;
    const update = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const next = readMobileNoticeViewport();
        setNoticeViewport(previous => previous.height === next.height && previous.top === next.top ? previous : next);
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
    };
  }, [access]);
  async function copyTrainingLink() {
    if (copyStatus === 'copying') return;
    if (copyResetRef.current !== null) window.clearTimeout(copyResetRef.current);
    setCopyStatus('copying');
    setManualCopyLink('');
    try {
      await copyCurrentTrainingLink();
      setCopyStatus('copied');
      copyResetRef.current = window.setTimeout(() => {
        copyResetRef.current = null;
        setCopyStatus('idle');
      }, 3000);
    } catch {
      // Let the user copy manually if their browser blocks clipboard access.
      setManualCopyLink(window.location.href);
      setCopyStatus('manual');
    }
  }
  // Small mobile screens show the prompt before training requests start.
  // Desktop browsers can resize freely without being blocked by this notice.
  if (access === 'allowed') return <HostTrainingDesktop/>;
  return <div className="host-training"><style>{STYLES}</style><style>{DESKTOP_STYLES}</style>
    <main className="ht-desktop-notice" style={{
      '--ht-notice-visible-height': noticeViewport.height ? `${noticeViewport.height}px` : undefined,
      '--ht-notice-visible-top': `${noticeViewport.top}px`,
    }}>
      <header className="ht-desktop-navbar">
        <TribeLogo className="ht-desktop-logo" role="img" aria-label="Tribe"/>
      </header>
      <div className="ht-desktop-body">
        <div className="ht-desktop-card">
          <DesktopBrowserIllustration/>
          <div className="ht-desktop-copy">
            <h1>Open on desktop</h1>
            <p>Open your training link on a laptop or desktop.</p>
            <button type="button" className="ht-copy-training-link" onClick={copyTrainingLink}
              disabled={copyStatus === 'copying'} aria-live="polite">
              {copyStatus === 'copied' ? 'Link copied ✓' : copyStatus === 'copying' ? 'Copying…' : 'Copy training link'}
            </button>
            {manualCopyLink && <div className="ht-copy-link-fallback">
              <p>Press and hold the link to copy it.</p>
              <input type="text" readOnly value={manualCopyLink} aria-label="Training link"
                onFocus={event => event.currentTarget.select()} onClick={event => event.currentTarget.select()}/>
            </div>}
            <a className="ht-back-home-link" href="/">Back to homepage</a>
          </div>
        </div>
      </div>
    </main>
  </div>;
}

// Keep the placeholder large, then fit typed email text to the visible field.
function fittedEmailFontSize(baseSize, availableWidth, textWidth) {
  if (textWidth <= availableWidth || textWidth <= 0) return baseSize;
  return Math.min(baseSize, Math.max(16, Math.floor(baseSize * availableWidth / textWidth)));
}

function HostTrainingDesktop() {
  const [screen, setScreen] = useState('access');
  const [email, setEmail] = useState(() => { const saved = storageRead(EMAIL_KEY, ''); return typeof saved === 'string' ? saved : ''; });
  const [training, setTraining] = useState(null);
  const [slide, setSlide] = useState(1);
  const [step, setStep] = useState(0);
  const [practiceReady,setPracticeReady]=useState({key:null,complete:false});
  const [confirmQuiz, setConfirmQuiz] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [questions, setQuestions] = useState([]);
  const [attemptId, setAttemptId] = useState('');
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizIndex, setQuizIndex] = useState(0);
  const [quizReview, setQuizReview] = useState(false);
  const [editingQuizAnswer, setEditingQuizAnswer] = useState(false);
  const mutex = useRef(false);
  const headingRef = useRef(null);
  const emailInputRef = useRef(null);
  const lesson = LESSONS[slide - 1];
  const check=sectionCheckFor(lesson.id);
  const practiceStorageKey=`tribeHostTrainingPractice:${email.trim().toLowerCase()}:${COURSE_VERSION}:${PRACTICE_VERSION}:${check?.id??''}`;
  const checkComplete=practiceReady.key===practiceStorageKey&&practiceReady.complete;
  const onPracticeReady=useCallback(complete=>setPracticeReady(previous=>previous.key===practiceStorageKey&&previous.complete===complete?previous:{key:practiceStorageKey,complete}),[practiceStorageKey]);
  const ActiveLesson = lesson.Component;
  const isCheck = Boolean(check && step >= lesson.parts.length);
  const audio = useHostTrainingAudio({ email, slide, part: step, enabled: screen === 'training' && !isCheck, api: API,
    narration: lesson.parts[step]?.narration || '',
    completionKey: narrationKey(email, COURSE_VERSION, lesson.id, step),
    previouslyCompleted: training?.completedSlides?.includes(slide) === true });
  const narrationLocked = !isCheck && !audio.listenComplete;

  // Prepare remaining parts while the current part plays. Prefetch only slides
  // the server has already unlocked; completing a lesson still requires its
  // narration and practice check.
  useEffect(() => {
    if (!training?.startedAt || training.quizInProgress || !training.canReviewTraining ||
        !['intro', 'training'].includes(screen) || (screen === 'training' && !isCheck && !audio.available)) return;
    const permitted = Math.min(LESSONS.length, (training.completedSlides || []).length + 1);
    const targets = [];
    for (let n = slide; n <= Math.min(permitted, slide + 1); n++) {
      const firstPart = n === slide ? (screen === 'intro' ? Math.min(step, LESSONS[n - 1].parts.length - 1) : step + 1) : 0;
      for (let p = firstPart; p < LESSONS[n - 1].parts.length; p++) targets.push(audioTarget(email, n, p));
    }
    let cancelled = false;
    (async () => {
      for (const target of targets.slice(0, 3)) {
        if (cancelled) break;
        await narrationClient.prefetch(target);
      }
    })();
    return () => { cancelled = true; };
  }, [email, screen, slide, step, isCheck, audio.available, training?.startedAt,
    training?.quizInProgress, training?.canReviewTraining, training?.completedSlides?.length]);


  // Continue narration within this lesson after a brief pause. The final part
  // stays on screen until the learner chooses Continue.
  useEffect(() => {
    if (screen !== 'training' || isCheck || !audio.listenComplete || audio.status !== 'Complete' ||
        step >= lesson.parts.length - 1) return undefined;
    const timer = window.setTimeout(() => {
      setStep(current => current === step ? current + 1 : current);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [screen, slide, step, isCheck, lesson.parts.length, audio.status, audio.listenComplete]);

  useEffect(() => {
    setError('');
    if (screen !== 'access') headingRef.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [screen, slide, step, quizIndex, quizReview]);

  useEffect(() => {
    if (screen === 'training') storageWrite(cursorKey(email, COURSE_VERSION), { slide, step });
  }, [screen, slide, step, email]);

  useLayoutEffect(() => {
    const input = emailInputRef.current;
    if (screen !== 'access' || !input) return undefined;
    let active = true;
    const measure = document.createElement('span');
    measure.style.cssText = 'all:initial;position:fixed;left:-10000px;top:0;visibility:hidden;white-space:pre;pointer-events:none;';
    measure.setAttribute('aria-hidden', 'true');
    document.body.appendChild(measure);
    const fit = () => {
      if (!active) return;
      input.style.fontSize = '';
      if (!input.value) return;
      const css = window.getComputedStyle(input);
      const baseSize = parseFloat(css.fontSize);
      const availableWidth = input.clientWidth - (parseFloat(css.paddingLeft) || 0) - (parseFloat(css.paddingRight) || 0) - 12;
      if (!Number.isFinite(baseSize) || availableWidth <= 0) return;
      measure.style.fontFamily = css.fontFamily;
      measure.style.fontWeight = css.fontWeight;
      measure.style.fontStyle = css.fontStyle;
      measure.style.fontSize = css.fontSize;
      measure.style.letterSpacing = css.letterSpacing;
      measure.textContent = input.value;
      const size = fittedEmailFontSize(baseSize, availableWidth, measure.getBoundingClientRect().width);
      input.style.fontSize = `${size}px`;
    };
    fit();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(fit) : null;
    observer?.observe(input.parentElement);
    window.addEventListener('resize', fit);
    document.fonts?.ready.then(fit);
    document.fonts?.addEventListener('loadingdone', fit);
    return () => {
      active = false;
      observer?.disconnect();
      window.removeEventListener('resize', fit);
      document.fonts?.removeEventListener('loadingdone', fit);
      measure.remove();
    };
  }, [email, screen]);

  async function request(path, body = {}, method = 'post') {
    const { data } = await axios({ method, url: `${API}/host-onboarding/training/${path}`, data: { email, ...body } });
    return data;
  }
  async function run(work) {
    if (mutex.current) return;
    mutex.current = true; setLoading(true); setError('');
    try { await work(); }
    catch (err) { setError(err.response?.data?.message || err.message || 'Something went wrong. Please try again.'); }
    finally { mutex.current = false; setLoading(false); }
  }
  function update(data) { if (data.training) setTraining(data.training); }
  function adoptQuiz(data, identity = email) {
    if (!Array.isArray(data.questions) || !data.questions.length || !data.attemptId) throw new Error('Unable to load this quiz. Please try again.');
    const draft = sanitizeQuizDraft(data.questions, storageRead(quizKey(identity, data.attemptId)));
    update(data); setQuestions(data.questions); setAttemptId(data.attemptId);
    setQuizAnswers(draft.answers); setQuizIndex(draft.index); setQuizReview(false); setEditingQuizAnswer(false);
    audio.stop(); setConfirmQuiz(false); setScreen('quiz');
  }
  async function checkAccess(event) {
    event?.preventDefault();
    const identity = email.trim().toLowerCase();
    await run(async () => {
      const data = await request('access', { email: identity });
      verifyCourse(data, LESSONS, COURSE_VERSION);
      storageWrite(EMAIL_KEY, identity); setEmail(identity); update(data);
      const cursor = restoreCursor(data.training, LESSONS, storageRead(cursorKey(identity, COURSE_VERSION)), identity, COURSE_VERSION);
      setSlide(cursor.slide); setStep(cursor.step);
      const next = entryScreen(data.training);
      if (next === 'quiz') adoptQuiz(await request('quiz/start', { email: identity }), identity);
      else setScreen(next);
    });
  }
  async function startLessons(review = false) {
    await run(async () => {
      const data = await request('start'); update(data);
      if (review) {
        update(await request('progress', { currentSlide: 1 }, 'patch'));
        setSlide(1); setStep(0);
      } else {
        const cursor = restoreCursor(data.training, LESSONS, storageRead(cursorKey(email, COURSE_VERSION)), email, COURSE_VERSION);
        setSlide(cursor.slide); setStep(cursor.step);
      }
      setScreen('training');
    });
  }
  async function forward() {
    if (narrationLocked || (isCheck && !checkComplete)) return;
    if (step < lesson.parts.length - 1) { audio.stop(); setStep(step + 1); return; }
    if (check && !isCheck) { audio.stop(); setStep(lesson.parts.length); return; }
    await run(async () => {
      const next = Math.min(LESSONS.length, slide + 1);
      update(await request('progress', { completedSlide: slide, currentSlide: next }, 'patch'));
      if (slide === LESSONS.length) {
        const data = await request('complete'); update(data);
        audio.stop();
        // A passed applicant may still review after using every attempt.
        if (data.training.quizAttemptsRemaining > 0) setConfirmQuiz(true);
        else setScreen('result');
      } else { audio.stop(); setSlide(next); setStep(0); }
    });
  }
  async function back() {
    if (step > 0) { audio.stop(); setStep(step - 1); return; }
    if (slide === 1) return;
    await run(async () => {
      update(await request('progress', { currentSlide: slide - 1 }, 'patch'));
      audio.stop(); setSlide(slide - 1); setStep(0);
    });
  }
  async function startQuiz() { await run(async () => adoptQuiz(await request('quiz/start'))); }
  function persistDraft(answers, index) { storageWrite(quizKey(email, attemptId), { answers, index }); }
  function selectQuiz(value) {
    const next = { ...quizAnswers, [questions[quizIndex].questionId]: value };
    setQuizAnswers(next); persistDraft(next, quizIndex);
  }
  function moveQuiz(index) { setQuizIndex(index); setQuizReview(false); persistDraft(quizAnswers, index); }
  function editQuizAnswer(index) { setEditingQuizAnswer(true); moveQuiz(index); }
  function returnToQuizReview() { setEditingQuizAnswer(false); setQuizReview(true); }
  async function submitQuiz() {
    if (!questions.every(q => quizAnswers[q.questionId])) return;
    await run(async () => {
      const data = await request('quiz', { attemptId, answers: questions.map(q => ({ questionId: q.questionId, answer: quizAnswers[q.questionId] })) });
      update(data); storageRemove(quizKey(email, attemptId)); setScreen('result');
    });
  }
  function changeEmail() {
    audio.stop(); setScreen('access'); setTraining(null); setQuestions([]); setQuizAnswers({}); setAttemptId(''); setConfirmQuiz(false);
  }

  const mainHeader = <header className="ht-header"><TribeLogo className="ht-logo" role="img" aria-label="Tribe"/>
    {screen === 'training' ? <Progress slide={slide}/> : screen === 'quiz' ? <QuizProgress index={quizIndex} total={questions.length}/> : <span/>}
    <span className="ht-module-name">HOST TRAINING MODULE</span></header>;
  const errorBlock = error ? <div className="ht-error" role="alert">{error}<button className="ht-text-button" disabled={loading} onClick={() => checkAccess()}>Refresh training</button></div> : null;
  const footer = <div className="ht-document-footer">© 2026 Tribe, Inc. All rights reserved.</div>;
  const passed = Boolean(training?.quizPassedAt);
  const lastResult = training?.lastQuizResult;

  const overviewSlide = Math.max(1, Math.min(LESSONS.length, Number(training?.currentSlide) || 1));
  const visibleStart = Math.max(0, Math.min(LESSONS.length - 6, overviewSlide - 3));
  const visibleLessons = LESSONS.slice(visibleStart, visibleStart + 6);
  const ending = overviewSlide >= LESSONS.length - 2;
  const endShift = Math.min(54, Math.max(0, overviewSlide - (LESSONS.length - 3)) * 18);

  if (screen === 'access') return <div className="host-training"><style>{STYLES}</style>
    <main className="access-ambient">
      <header className="access-ambient-header">
        <TribeLogo className="training-logo" role="img" aria-label="Tribe" />
        <div className="access-module-name">HOST TRAINING MODULE</div>
      </header>
      <div className="access-ambient-stage" aria-hidden="true">
        <div className="ambient-card ambient-dashboard">
          <div className="ambient-dashboard-top"><span className="ambient-dot" /><strong>Event guide</strong></div>
          <div className="ambient-dashboard-row"><span className="ambient-avatar" /><span className="ambient-guide-label">Welcome guests</span><span className="ambient-pill">4:00</span></div>
          <div className="ambient-dashboard-row"><span className="ambient-avatar" /><span className="ambient-guide-label">First activity</span><span className="ambient-pill ambient-pill-dark">4:15</span></div>
        </div>
        <div className="ambient-float ambient-profile"><span className="ambient-profile-avatar">M</span><div><strong>Welcome your guests</strong><small>Help everyone join the group</small></div></div>
        <div className="ambient-float ambient-message"><span className="ambient-message-dot" /><p>Hi everyone! I’m at the main entrance. Here’s how to find me.</p></div>
        <div className="ambient-float ambient-qualified"><span>✓</span><div><strong>Guest checked in</strong><small>Attendance recorded in the portal</small></div></div>
        <div className="ambient-float ambient-step"><span>01</span><strong>Follow the event guide</strong></div>
      </div>
      <section className="access-ambient-content">
        <h1 ref={headingRef} tabIndex={-1}>Get started with your email</h1>
        <form onSubmit={checkAccess}>
          <div className="access-prompt-input"><input ref={emailInputRef} id="host-training-email" type="email" required autoFocus autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="type here" aria-label="Application email" disabled={loading} /></div>
          <button type="submit" className="training-button is-wide" disabled={loading}>{loading ? 'Checking…' : 'Get Started'}{!loading && <LuArrowRight aria-hidden="true" style={{ width: 16, height: 16, marginLeft: 10, verticalAlign: 'middle' }} />}</button>
          <p className="access-form-note">This module covers how to prepare for an event, lead the experience, and help guests connect. Use the email from your host application to begin.</p>
          {errorBlock}
        </form>
      </section>
      <footer className="access-ambient-footer"><div className="access-legal">© 2026 Tribe, Inc. All rights reserved.</div>
        <div className="access-centered-socials"><a href="https://www.instagram.com/jointribe.io/" target="_blank" rel="noreferrer" aria-label="Instagram"><FaInstagram /></a><a href="https://www.linkedin.com/company/82302259" target="_blank" rel="noreferrer" aria-label="LinkedIn"><FaLinkedin /></a></div>
      </footer>
    </main>
  </div>;

  if (screen === 'intro') return <div className="host-training"><style>{STYLES}</style>
    <main className="intro-open">
      <header className="intro-open-header"><TribeLogo className="training-logo" role="img" aria-label="Tribe" /><div className="intro-open-module-name">HOST TRAINING MODULE</div></header>
      <div className="intro-background-details" aria-hidden="true">
        <div className="intro-bg-card intro-bg-card-one"><span className="intro-bg-avatar">M</span><div><strong>Event guide</strong><small>Museum & Wine Bar · Los Angeles</small></div><span className="intro-bg-pill">PLAN</span></div>
        <div className="intro-bg-card intro-bg-card-two intro-bg-outreach"><span className="intro-bg-dot" /><p>I’m at the meeting point. Come say hello when you arrive!</p></div>
        <div className="intro-bg-card intro-bg-card-three"><span className="intro-bg-check">✓</span><div><strong>Guests checked in</strong><small>Ready to start the experience</small></div></div>
      </div>
      <section className="intro-open-main">
        <div className="intro-open-copy">
          <h1 ref={headingRef} tabIndex={-1}>Host Training Overview</h1>
          <p>This training walks you through how to prepare for and lead a Tribe event, help guests connect, and meet our expectations for hosts. It includes {LESSONS.length} short lessons and short practice checks throughout the lessons. After completing the lessons, you’ll have up to 4 attempts to pass the final quiz.</p>
          <div className="intro-open-stats"><div><strong>{LESSONS.length}</strong><span>lessons</span></div><div><strong>{training?.bestQuizScore == null ? 'N/A' : `${Math.round(training.bestQuizScore)}%`}</strong><span>quiz score</span></div><div className="intro-account-stat" title={email}><strong>{email || '—'}</strong><span>applicant</span></div></div>
          <div className="intro-open-actions">
            <button type="button" className={`training-button${passed || training?.quizAttemptsExhausted ? ' intro-status-button' : ''}`} disabled={loading || passed || training?.quizAttemptsExhausted} onClick={() => training?.moduleCompletedAt ? setConfirmQuiz(true) : startLessons()}>
              {passed && <LuCheck size={15} />}{loading ? 'Loading…' : passed ? 'Passed' : training?.quizAttemptsExhausted ? 'Not passed' : training?.moduleCompletedAt ? (training?.quizAttempts > 0 ? 'Retake quiz' : 'Start final quiz') : training?.startedAt ? 'Continue training' : 'Start training'}
            </button>
            {training?.moduleCompletedAt && training?.canReviewTraining && <button type="button" className="intro-review-content" disabled={loading} onClick={() => startLessons(true)}>Review content</button>}
            {passed && training?.quizAttemptsRemaining > 0 && <button type="button" className="intro-review-content" disabled={loading} onClick={() => setConfirmQuiz(true)}>Improve your score</button>}
          </div>
          {errorBlock}
        </div>
        <div className={`intro-stepper${ending ? ' is-ending' : ''}`} aria-label="Training progress"><div className="intro-stepper-list" style={{ transform: endShift ? `translateY(-${endShift}px)` : 'translateY(0)' }}>
          {visibleLessons.map(item => { const done = training?.completedSlides?.includes(item.slide); const current = !training?.moduleCompletedAt && item.slide === overviewSlide; return <div key={item.id} className={`intro-stepper-item${done ? ' is-complete' : ''}${current ? ' is-current' : ''}`}>
            {current && <div className="intro-you-are-here">YOU'RE HERE</div>}<span className="intro-stepper-node">{done ? '✓' : ''}</span><div className="intro-stepper-copy"><strong>{item.title}</strong>{current && <small>Current lesson</small>}</div>
          </div>; })}
        </div></div>
      </section>
      <footer className="intro-open-footer"><span>© 2026 Tribe, Inc. All rights reserved.</span><button type="button" className="intro-change-email" onClick={changeEmail}>Use a different email</button></footer>
    </main>
    {confirmQuiz && <ConfirmQuiz training={training} loading={loading} error={error} onCancel={() => setConfirmQuiz(false)} onConfirm={startQuiz} />}
  </div>;

  return <div className="host-training"><style>{STYLES}</style>
    <audio ref={audio.audioRef} {...audio.events} preload="auto"/>
    <main className={`ht-paper ${screen === 'quiz' ? 'is-quiz' : ''}`}>
      {mainHeader}
      {screen === 'training' && <>
        <section className="ht-stage" key={`${slide}-${step}`}>
          {isCheck ? <SectionQuiz key={practiceStorageKey} check={check} storageKey={practiceStorageKey} onReady={onPracticeReady} headingRef={headingRef}/> : <div className="ht-lesson-layout"><div className="ht-lesson-copy">
            <span className="ht-eyebrow">{lesson.section}{lesson.parts.length > 1 ? ` / PART ${step + 1} OF ${lesson.parts.length}` : ''}</span>
            <h1 ref={headingRef} tabIndex={-1}>{lesson.title}</h1>
            <div className="ht-summary">{lesson.summary.map(text => <p key={text}>{text}</p>)}</div>
            <details className="ht-transcript"><summary>Read the transcript</summary><p>{lesson.parts[step].narration}</p></details>
          </div><div className={`ht-lesson-visual scene-${lesson.id}`}><ActiveLesson key={`${lesson.id}-${step}`} part={step} time={audio.time} duration={audio.duration} wordTimings={audio.wordTimings} activeWordIndex={audio.activeWordIndex} audioTranscript={audio.transcript} audioStatus={audio.status} audioRef={audio.audioRef}/></div></div>}
        </section>
        {audio.audioError && !isCheck && <div className="ht-audio-note" role="status">{audio.audioError}<button className="ht-text-button" onClick={audio.retry}>Try audio again</button></div>}
        {errorBlock}
        {narrationLocked && <p className="ht-listen-note" id="ht-listen-note" role="status">Finish the narration to continue.</p>}
        <footer className="ht-controls"><div className="ht-control-left"><Button secondary onClick={back} disabled={loading || (slide === 1 && step === 0)}><LuArrowLeft/> Back</Button>
          {!isCheck && <><button className="ht-audio-button" onClick={audio.toggle} disabled={!audio.available} aria-label={audio.status === 'Playing' ? 'Pause narration' : 'Play narration'}>{audio.status === 'Playing' ? <LuPause/> : <LuPlay/>}</button>
            <button className="ht-audio-button" onClick={audio.replay} disabled={!audio.available} aria-label="Replay narration"><LuRotateCcw/></button>
            <small>{audio.available ? `${clock(audio.time)} / ${clock(audio.duration)}` : audio.status}</small>
            <label className="ht-speed"><span className="ht-sr-only">Playback speed</span><select value={audio.rate} onChange={e => audio.setRate(Number(e.target.value))}><option value={1}>1×</option><option value={1.25}>1.25×</option><option value={1.5}>1.5×</option></select></label></>}
        </div><Button onClick={forward} disabled={loading || narrationLocked || (isCheck && !checkComplete)} aria-describedby={narrationLocked ? 'ht-listen-note' : undefined}>{loading ? 'Saving…' : slide === LESSONS.length && step === lesson.parts.length - 1 ? (training?.quizAttemptsRemaining === 0 ? 'View results' : 'Start final quiz') : 'Continue'} <LuArrowRight/></Button></footer>
      </>}

      {screen === 'quiz' && <section className={`ht-section-check ht-quiz${quizReview ? ' is-reviewing' : ''}`} aria-label="Final quiz">
        <div className="section-check-meta"><span>Final quiz</span><span>{quizReview ? `${questions.filter(q => quizAnswers[q.questionId]).length} / ${questions.length} answered` : `${quizIndex + 1} / ${questions.length}`}</span></div>
        {quizReview ? <><h1 ref={headingRef} tabIndex={-1}>Review your answers</h1><p className="section-question-help">Check your answers below. You can edit any answer before submitting your quiz.</p>
          <ol className="ht-final-review-list">{questions.map((q, i) => {
            const selectedAnswer = q.choices.find(choice => choice.value === quizAnswers[q.questionId]);
            return <li key={q.questionId} className="ht-final-review-item">
              <span className="ht-final-review-number" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
              <div className="ht-final-review-copy">
                <h2 className="ht-final-review-question">{q.prompt}</h2>
                <div className="ht-final-review-answer"><span className="ht-sr-only">Your answer: </span>{selectedAnswer && <span className="ht-final-review-letter">{selectedAnswer.value}.</span>}<p>{selectedAnswer?.label || 'Not answered'}</p></div>
              </div>
              <button type="button" className="ht-final-review-edit" onClick={() => editQuizAnswer(i)} aria-label={`Edit answer to question ${i + 1}: ${q.prompt}`} disabled={loading}>Edit</button>
            </li>;
          })}</ol>
          <div className="section-question-actions"><button type="button" className="section-quiet-button" onClick={() => { setEditingQuizAnswer(false); setQuizReview(false); }} disabled={loading}>Back to questions</button><button type="button" className="section-check-button" onClick={submitQuiz} disabled={loading || !questions.every(q => quizAnswers[q.questionId])}>{loading ? 'Submitting…' : 'Submit quiz'}</button></div>
        </> : <><h1 ref={headingRef} tabIndex={-1}>{questions[quizIndex]?.prompt}</h1>
          <fieldset className="section-choice-list"><legend className="ht-sr-only">Choose one answer</legend>{questions[quizIndex]?.choices.map(choice => <label key={choice.value} className={quizAnswers[questions[quizIndex].questionId] === choice.value ? 'is-selected' : ''}>
            <input type="radio" name={questions[quizIndex].questionId} value={choice.value} checked={quizAnswers[questions[quizIndex].questionId] === choice.value} onChange={() => selectQuiz(choice.value)} disabled={loading}/><span>{choice.value}</span><strong>{choice.label}</strong></label>)}</fieldset>
          <div className="section-question-actions">{!editingQuizAnswer && quizIndex > 0 && <button type="button" className="section-quiet-button" onClick={() => moveQuiz(quizIndex - 1)} disabled={loading}>Previous question</button>}<button type="button" className="section-check-button" onClick={() => editingQuizAnswer || quizIndex === questions.length - 1 ? returnToQuizReview() : moveQuiz(quizIndex + 1)} disabled={loading || !quizAnswers[questions[quizIndex]?.questionId]}>{editingQuizAnswer ? 'Return to review' : quizIndex === questions.length - 1 ? 'Review answers' : 'Next question'}</button></div>
        </>}
        {errorBlock}
      </section>}

      {screen === 'result' && <section className="ht-result"><span className="ht-eyebrow">HOST TRAINING / YOUR RESULTS</span>
        <h1 ref={headingRef} tabIndex={-1}>{passed ? 'You’ve passed.' : training?.quizAttemptsExhausted ? 'No attempts remaining.' : 'Review, then try again.'}</h1>
        <div className="ht-result-score"><strong>{training?.bestQuizScore ?? '—'}{training?.bestQuizScore != null ? '%' : ''}</strong><span>Best score · 85% required</span></div>
        {lastResult && <p>Latest attempt: {lastResult.score} of {lastResult.totalQuestions} correct ({lastResult.percentage}%).</p>}
        <p>{passed ? 'Your passing result is saved. The Tribe team will review it alongside your interview and follow up with a decision.' : training?.quizAttemptsExhausted ? 'You’ve used all four attempts without passing. We won’t be able to move forward with your application.' : 'Review the lessons before your next attempt. You can revisit the material and return to the quiz when you’re ready.'}</p>
        <div className="ht-result-stats"><span>{training?.quizAttempts || 0} of 4 attempts used</span><span>{training?.quizAttemptsRemaining || 0} remaining</span></div>
        <div className="ht-actions"><Button secondary onClick={() => setScreen('intro')} disabled={loading}>Back to overview</Button>{training?.canReviewTraining && <Button secondary onClick={() => startLessons(true)} disabled={loading}>Review training</Button>}
          {training?.quizAttemptsRemaining > 0 && <Button onClick={() => setConfirmQuiz(true)} disabled={loading}>{passed ? 'Improve your score' : 'Try again'} <LuArrowRight/></Button>}</div>
        <button className="ht-text-button ht-change-email" onClick={changeEmail}>Use a different email</button>{errorBlock}
      </section>}
      {footer}
    </main>
    {confirmQuiz && <ConfirmQuiz training={training} loading={loading} error={error} onCancel={() => setConfirmQuiz(false)} onConfirm={startQuiz}/>}
  </div>;
}

function QuizProgress({index,total}) {
  const current=total?index+1:0;
  return <div className="training-header-progress">
    <div className="training-progress-track" role="progressbar" aria-label="Final quiz progress" aria-valuemin={0} aria-valuemax={total} aria-valuenow={current} aria-valuetext={`Question ${current} of ${total}`}>
      <span className="training-progress-fill" style={{width:`${total?current/total*100:0}%`}}/>
    </div>
    <div className="training-progress-labels"><span className="training-progress-title">Final quiz</span><span className="training-progress-count">Question {current} of {total}</span></div>
  </div>;
}

// Playback and draft persistence stay in this master page.

function useHostTrainingAudio({ email, slide, part, enabled, api, narration, completionKey, previouslyCompleted = false }) {
  const audioRef = useRef(null);
  const requestRef = useRef(0);
  const furthestTimeRef = useRef(0);
  const [sourceKey, setSourceKey] = useState('');
  const [listenedKey, setListenedKey] = useState(null);
  const savedCompletion = useMemo(() => storageRead(completionKey) === true, [completionKey]);
  const listenComplete = previouslyCompleted || savedCompletion || listenedKey === completionKey;
  const [retry, setRetry] = useState(0);
  const [status, setStatus] = useState('');
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState(1);
  const [available, setAvailable] = useState(false);
  const [audioError, setAudioError] = useState('');
  const [wordTimings, setWordTimings] = useState([]);
  const [transcript, setTranscript] = useState('');

  useEffect(() => {
    const request = ++requestRef.current;
    const controller = new AbortController();
    const audio = audioRef.current;
    let url = '';
    furthestTimeRef.current = 0; setSourceKey('');
    setTime(0); setDuration(0); setAvailable(false); setAudioError(''); setWordTimings([]); setTranscript('');
    if (audio) { audio.pause(); audio.removeAttribute('src'); audio.removeAttribute('data-narration-key'); audio.load(); }
    if (!enabled) { setStatus(''); return () => controller.abort(); }
    setStatus('Loading audio…');
    // A shared request keeps preloading and playback from generating the same
    // narration twice. Navigating away only ignores the result; it can finish
    // filling the cache for the next visit.
    narrationClient.get({email, slide, part, api, narration, courseVersion:COURSE_VERSION, audioVersion:AUDIO_VERSION})
      .then(async payload => {
      if (controller.signal.aborted || request !== requestRef.current) return;
      const audioBlob = payload.blob;
      setWordTimings(payload.words); setTranscript(payload.transcript);
      if (payload.duration > 0) setDuration(payload.duration);
      url = URL.createObjectURL(audioBlob);
      if (!audio) return;
      audio.dataset.narrationKey = completionKey;
      audio.src = url; audio.load(); setSourceKey(completionKey); setAvailable(true);
      try { await audio.play(); }
      catch { if (!controller.signal.aborted) setStatus('Press play to listen'); }
    }).catch(async err => {
      if (controller.signal.aborted) return;
      let message = err.message || 'Audio is unavailable. Please retry the narration to continue.';
      try {
        const blob = err.response?.data;
        if (blob?.text) message = JSON.parse(await blob.text()).message || message;
      } catch { /* Keep the useful fallback. */ }
      if (!controller.signal.aborted && request === requestRef.current) { setAudioError(message); setStatus('Audio unavailable'); }
    });
    return () => {
      controller.abort(); requestRef.current++;
      if (audio) { audio.pause(); audio.removeAttribute('src'); audio.removeAttribute('data-narration-key'); audio.load(); }
      if (url) URL.revokeObjectURL(url);
    };
  }, [email, slide, part, enabled, api, narration, retry, completionKey]);

  // Audio time is the only clock. Update immediately at word boundaries and
  // otherwise at most ten times per second; stop the loop while paused.
  useEffect(() => {
    if (status !== 'Playing') return undefined;
    let frame;
    let lastTime = -1;
    let lastWord = -2;
    const tick = () => {
      const audio = audioRef.current;
      if (!audio || audio.paused || audio.ended || audio.dataset.narrationKey !== completionKey) return;
      const now = audio.currentTime;
      if (!audio.seeking) furthestTimeRef.current = Math.max(furthestTimeRef.current, now);
      const word = findActiveWord(wordTimings, now);
      if (word !== lastWord || Math.abs(now - lastTime) >= 0.1) {
        lastTime = now; lastWord = word; setTime(now);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [status, wordTimings, completionKey]);

  useEffect(() => { if (audioRef.current) audioRef.current.playbackRate = rate; }, [rate, available]);
  const toggle = useCallback(async () => {
    const audio = audioRef.current;
    if (!enabled || !audio?.src || audio.dataset.narrationKey !== completionKey) return;
    if (!audio.paused) { audio.pause(); return; }
    try { await audio.play(); } catch { setStatus('Unable to play audio'); }
  }, [enabled, completionKey]);
  const replay = useCallback(async () => {
    const audio = audioRef.current;
    if (!enabled || !audio?.src || audio.dataset.narrationKey !== completionKey) return;
    audio.currentTime = 0;
    try { await audio.play(); } catch { setStatus('Press play to listen'); }
  }, [enabled, completionKey]);
  const stop = useCallback(() => { audioRef.current?.pause(); }, []);
  function currentAudio() {
    const audio = audioRef.current;
    return enabled && audio?.src && audio.dataset.narrationKey === completionKey ? audio : null;
  }
  function syncTime(record = false) {
    const audio = currentAudio();
    if (!audio) return;
    if (record && !audio.seeking) furthestTimeRef.current = Math.max(furthestTimeRef.current, audio.currentTime);
    setTime(audio.currentTime || 0);
  }
  function preventSkip() {
    const audio = currentAudio();
    if (!audio) return;
    if (!listenComplete && audio.currentTime > furthestTimeRef.current + 0.05) {
      audio.currentTime = furthestTimeRef.current;
    }
    setTime(audio.currentTime || 0);
  }
  const currentSource = sourceKey === completionKey;
  return { audioRef, listenComplete,
    status: currentSource ? status : enabled ? 'Loading audio…' : '',
    time: currentSource ? time : 0, duration: currentSource ? duration : 0,
    rate, available: currentSource && available, audioError,
    wordTimings: currentSource ? wordTimings : [], transcript: currentSource ? transcript : '',
    activeWordIndex: currentSource ? findActiveWord(wordTimings, time) : -1,
    toggle, replay, stop, setRate, retry: async () => {
      await narrationClient.forget({email, slide, part, api, narration, courseVersion:COURSE_VERSION, audioVersion:AUDIO_VERSION});
      setRetry(r => r + 1);
    },
    events: {
      onPlay: () => { if (currentAudio()) setStatus('Playing'); },
      onPause: () => { const audio = currentAudio(); if (!audio) return; syncTime(); if (!audio.ended) setStatus('Paused'); },
      onEnded: () => {
        const audio = currentAudio();
        if (!audio) return;
        syncTime();
        if (!listenComplete && !hasPlayedNarration(audio)) {
          setStatus('Paused');
          setAudioError('Finish the full narration before continuing. Replay it to listen from the beginning.');
          return;
        }
        storageWrite(completionKey, true); setListenedKey(completionKey);
        setAudioError(''); setStatus('Complete');
      },
      onSeeking: preventSkip,
      onSeeked: () => syncTime(),
      onTimeUpdate: () => syncTime(true),
      onLoadedMetadata: () => { const audio = currentAudio(); if (audio) setDuration(Number.isFinite(audio.duration) ? audio.duration : 0); },
      onError: () => { if (currentAudio()) { setStatus('Audio unavailable'); setAudioError('Unable to play this audio. Retry the narration to continue.'); } },
    },
  };
}

// -1 during silence; repeated words retain their own timestamp/index.
function findActiveWord(words, time) {
  let low = 0, high = words.length - 1, index = -1;
  while (low <= high) {
    const middle = (low + high) >> 1;
    if (words[middle].start <= time) { index = middle; low = middle + 1; }
    else high = middle - 1;
  }
  return index >= 0 && time < words[index].end ? index : -1;
}

function storageRead(key, fallback = null) {
  try { const raw = window.localStorage.getItem(key); return raw ? JSON.parse(raw) : fallback; }
  catch { return fallback; }
}
function storageWrite(key, value) {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* Private mode/storage quota. */ }
}
function storageRemove(key) {
  try { window.localStorage.removeItem(key); } catch { /* Storage can be unavailable. */ }
}
function narrationKey(email, version, lessonId, part) { return `tribeHostTrainingListened:${version}:${email.trim().toLowerCase()}:${lessonId}:${part}`; }
function hasPlayedNarration(audio) {
  const duration = audio.duration, played = audio.played;
  if (!Number.isFinite(duration) || duration <= 0 || !played?.length) return false;
  const epsilon = Math.min(0.05, duration * 0.005);
  let covered = 0;
  for (let index = 0; index < played.length; index += 1) {
    if (played.start(index) > covered + epsilon) return false;
    covered = Math.max(covered, played.end(index));
    if (covered >= duration - epsilon) return true;
  }
  return false;
}
function cursorKey(email, version) { return `tribeHostTraining:${version}:${email}:cursor`; }
function quizKey(email, attemptId) { return `tribeHostTraining:${email}:quiz:${attemptId}`; }

function restoreCursor(training, lessons, saved, email, version) {
  const slide = Math.max(1, Math.min(lessons.length, Number(training?.currentSlide) || 1));
  const current=lessons[slide-1];
  const maximum=sectionCheckFor(current.id)?current.parts.length:current.parts.length-1;
  const part=saved?.slide===slide&&Number.isInteger(saved.step)?Math.max(0,Math.min(maximum,saved.step)):0;
  // A saved cursor cannot skip a first-listen part or jump straight to its check.
  const firstUnheard = training?.completedSlides?.includes(slide) ? -1
    : current.parts.findIndex((_, index) => storageRead(narrationKey(email, version, current.id, index)) !== true);
  return { slide, step: firstUnheard < 0 ? part : Math.min(part, firstUnheard) };
}
function sanitizeQuizDraft(questions, saved) {
  const answers = {};
  for (const q of questions) {
    const value = saved?.answers?.[q.questionId];
    if (q.choices.some(choice => choice.value === value)) answers[q.questionId] = value;
  }
  const firstMissing = questions.findIndex(q => !answers[q.questionId]);
  const index = Number.isInteger(saved?.index) && saved.index >= 0 && saved.index < questions.length
    ? saved.index : Math.max(0, firstMissing);
  return { answers, index };
}
function entryScreen(training) {
  if (training?.quizInProgress) return 'quiz';
  if (training?.quizPassedAt || training?.quizAttemptsExhausted ||
      (training?.moduleCompletedAt && training?.quizAttempts > 0)) return 'result';
  return 'intro';
}
function verifyCourse(data, lessons, version) {
  if (data.training?.courseVersion !== version || data.training?.totalSlides !== lessons.length ||
      !Array.isArray(data.sections) || data.sections.length !== lessons.length ||
      data.sections.some((section, i) => section.slide !== lessons[i].slide ||
        section.id !== lessons[i].id || section.partCount !== lessons[i].parts.length)) {
    throw new Error('This training page needs an update. Please contact the Tribe team.');
  }
}

// Player styles remain inline. Practice styles are scoped in the quiz components.
const STYLES = `
/* Scoped to this page. Typography, white paper, muted canvas, and controls follow
   the supplied ReferralPartnerTraining layout. */
.host-training {
  --ht-ink:#111; --ht-muted:#707070; --ht-line:#dddddd; --ht-soft:#f5f5f5;
  min-height:100vh; background:#f6f6f4; color:var(--ht-ink);
  font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
}
.host-training *, .host-training *::before, .host-training *::after { box-sizing:border-box; }
.host-training button, .host-training input, .host-training select { font:inherit; }
.host-training button { cursor:pointer; }
.host-training button:disabled { cursor:default; opacity:.42; }
.host-training button:focus-visible, .host-training input:focus-visible,
.host-training select:focus-visible, .host-training summary:focus-visible { outline:2px solid #111; outline-offset:4px; }
.host-training h1:focus { outline:none; }
.host-training .ht-paper { width:calc(100% - 56px); max-width:1180px; min-height:100vh; margin:0 auto; padding:14px 42px 16px; background:#fff; display:flex; flex-direction:column; }
.host-training .ht-header { min-height:72px; display:grid; grid-template-columns:1fr minmax(200px,360px) 1fr; gap:24px; align-items:center; }
.host-training .ht-logo { width:92px; height:auto; display:block; }
.host-training .ht-module-name { justify-self:end; font-size:11px; font-weight:700; letter-spacing:.055em; white-space:nowrap; }
.host-training .ht-document-footer { margin-top:auto; padding-top:20px; color:#a0a09b; font-size:9px; }
.host-training .ht-eyebrow, .host-training .ht-caption { display:block; font-size:10px; font-weight:750; letter-spacing:.09em; text-transform:uppercase; color:#797975; }
.host-training h1 { margin:16px 0 24px; font-size:clamp(34px,3.8vw,58px); line-height:1.04; letter-spacing:-.05em; font-weight:700; }
.host-training h2 { margin:10px 0; font-size:28px; line-height:1.1; letter-spacing:-.035em; }
.host-training h3 { margin:0; font-size:17px; letter-spacing:-.02em; }
.host-training p { line-height:1.6; }
.host-training .ht-button { min-height:46px; padding:12px 24px; display:inline-flex; align-items:center; justify-content:center; gap:9px; border:1px solid #111; border-radius:999px; background:#111; color:#fff; font-size:13px; font-weight:650; text-decoration:none; }
.host-training .ht-button:not(:disabled):hover { background:#333; }
.host-training .ht-button.is-secondary { color:#111; background:#fff; border-color:#ddd; }
.host-training .ht-button.is-secondary:not(:disabled):hover { border-color:#111; background:#fafafa; }
.host-training .ht-text-button { padding:7px 0; border:0; background:transparent; color:#777772; font-size:12px; text-align:left; }
.host-training .ht-text-button:hover { color:#111; }
.host-training .ht-actions { display:flex; align-items:center; flex-wrap:wrap; gap:18px; margin-top:30px; }
.host-training .ht-error { margin:18px 0; padding:13px 16px; border:1px solid #e7c3c0; border-radius:12px; color:#9a302b; background:#fff6f5; font-size:13px; line-height:1.5; }
.host-training .ht-error .ht-text-button { display:block; margin-top:4px; text-decoration:underline; }
.host-training .ht-sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
.host-training .ht-access { position:relative; flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; padding:100px 0; }
.host-training .ht-access h1 { font-size:clamp(30px,3vw,38px); font-weight:600; margin:18px 0; letter-spacing:-.035em; }
.host-training .ht-access form { position:relative; z-index:1; width:min(100%,640px); }
.host-training .ht-access input { width:100%; min-width:0; margin:12px 0 32px; padding:12px 0; border:0; background:transparent; font-size:clamp(30px,5vw,60px); font-weight:300; letter-spacing:-.05em; text-align:center; outline:none; }
.host-training .ht-access input::placeholder { color:#d7d7dc; }
.host-training .ht-access input:focus-visible { outline:0; border-bottom:1px solid #aaa; }
.host-training .ht-access > p { max-width:480px; color:#858580; font-size:12px; margin-top:25px; }
.host-training .ht-access-orbit { position:absolute; inset:0; pointer-events:none; }
.host-training .ht-access-orbit span { position:absolute; padding:12px 18px; border:1px solid #eee; border-radius:13px; color:#9c9c94; background:#fafaf9; font-size:11px; }
.host-training .ht-access-orbit span:nth-child(1) { top:10%; left:15%; transform:rotate(-7deg); }
.host-training .ht-access-orbit span:nth-child(2) { top:16%; right:6%; transform:rotate(6deg); }
.host-training .ht-access-orbit span:nth-child(3) { bottom:10%; right:18%; transform:rotate(-4deg); }
.host-training .ht-intro { display:grid; grid-template-columns:1.1fr 1fr; gap:9%; align-items:center; flex:1; padding:70px 15px; }
.host-training .ht-intro h1 { font-size:clamp(34px,3.4vw,48px); font-weight:600; }
.host-training .ht-intro p { color:#686864; font-size:16px; }
.host-training .ht-intro-stats { display:flex; gap:36px; margin:34px 0; }
.host-training .ht-intro-stats strong, .host-training .ht-intro-stats span { display:block; }
.host-training .ht-intro-stats strong { font-size:22px; font-weight:650; }
.host-training .ht-intro-stats span { margin-top:4px; color:#989893; font-size:11px; }
.host-training .ht-change-email { margin-top:18px; font-size:11px; }
.host-training .ht-intro-roadmap { padding:34px; background:#f7f7f5; border-radius:24px; }
.host-training .ht-intro-roadmap > p { font-size:12px; margin:24px 0 0; }
.host-training .ht-progress-dots { display:flex; align-items:center; justify-content:space-between; position:relative; }
.host-training .ht-progress-dots::before { content:""; height:2px; background:#e5e5e1; position:absolute; left:8px; right:8px; top:50%; }
.host-training .ht-progress-dots > span { position:relative; z-index:1; width:12px; height:12px; border:1px solid #ddd; border-radius:50%; background:#fff; display:grid; place-items:center; }
.host-training .ht-progress-dots > span.is-complete { background:#111; border-color:#111; color:white; }
.host-training .ht-progress-dots > span.is-current { box-shadow:0 0 0 4px #1111; border:2px solid #111; }
.host-training .ht-progress > small { display:block; text-align:center; font-size:10px; margin-top:12px; color:#707070; }
.host-training .ht-stage { flex:1; min-height:min(620px,calc(100vh - 205px)); display:grid; align-items:center; padding:34px 0; animation:ht-enter .25s ease both; }
.host-training .ht-lesson-layout { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1.06fr); gap:6%; align-items:center; padding:0 3%; }
.host-training .ht-summary p { color:#373737; font-size:18px; line-height:1.5; margin:0 0 10px; }
.host-training .ht-lesson-visual { min-height:400px; border-radius:25px; background:#f5f5f5; padding:30px; display:grid; align-items:center; overflow:hidden; }
.host-training .ht-transcript { margin-top:27px; color:#707070; font-size:12px; }
.host-training .ht-transcript summary { cursor:pointer; width:max-content; }
.host-training .ht-transcript[open] { padding-bottom:12px; }
.host-training .ht-transcript p { max-height:250px; overflow:auto; white-space:pre-line; color:#484848; font-size:14px; padding-right:12px; }
.host-training .ht-controls { min-height:70px; display:flex; align-items:center; justify-content:space-between; gap:16px; }
.host-training .ht-control-left { display:flex; gap:9px; align-items:center; flex-wrap:wrap; }
.host-training .ht-audio-button { border:0; background:transparent; padding:8px; color:#777; display:grid; place-items:center; }
.host-training .ht-controls small { color:#8a8a85; font-size:10px; }
.host-training .ht-speed select { font-size:11px; color:#777; background:transparent; border:0; padding:4px; }
.host-training .ht-listen-note { margin:0 0 12px; color:#777; font-size:12px; line-height:1.5; text-align:right; }
.host-training .ht-audio-note { display:flex; align-items:center; gap:14px; color:#777; font-size:12px; }
.host-training .ht-roadmap { display:grid; }
.host-training .ht-roadmap > div { display:flex; align-items:center; gap:25px; padding:22px 0; border-bottom:1px solid #deded9; }
.host-training .ht-roadmap > div:last-child { border:0; }
.host-training .ht-roadmap span { font-size:12px; color:#999; font-weight:600; }
.host-training .ht-roadmap strong { font-size:25px; font-weight:600; letter-spacing:-.025em; }
.host-training .ht-rows { display:grid; }
.host-training .ht-row { display:grid; grid-template-columns:30px 1fr; gap:17px; padding:22px 0; }
.host-training .ht-row + .ht-row { border-top:1px solid #ddd; }
.host-training .ht-number { color:#aaa; font-size:11px; padding-top:4px; }
.host-training .ht-row p { color:#757575; font-size:13px; line-height:1.5; margin:7px 0 0; }
.host-training .ht-note { margin:24px 0 0; color:#81817a; font-size:12px; line-height:1.6; }
.host-training .ht-people { display:flex; justify-content:center; gap:12px; }
.host-training .ht-people span { border-radius:50%; width:60px; height:60px; display:grid; place-items:center; border:1px solid #ccc; font-size:20px; background:#fff; }
.host-training .ht-people span:nth-child(even) { background:#111; color:#fff; border-color:#111; }
.host-training .ht-connection-line { width:65%; height:30px; border:1px solid #ccc; border-top:0; margin:0 auto 32px; border-radius:0 0 18px 18px; }
.host-training .ht-connection-scene { text-align:center; }
.host-training .ht-connection-scene h2 { font-size:26px; }
.host-training .ht-connection-scene > p { color:#777; font-size:13px; }
.host-training .ht-small-labels { display:flex; justify-content:center; gap:10px; margin-top:25px; }
.host-training .ht-small-labels span { border:1px solid #d8d8d8; padding:8px 12px; border-radius:99px; font-size:10px; }
.host-training .ht-phase-flow { padding:5px 0; }
.host-training .ht-phase-flow > div { position:relative; display:grid; grid-template-columns:28px 1fr; gap:18px; align-items:center; padding:11px 0; }
.host-training .ht-phase-flow > div:not(:nth-child(7))::after { content:""; position:absolute; height:16px; width:1px; background:#ccc; top:39px; left:13px; }
.host-training .ht-phase-flow > div > span { width:28px; height:28px; display:grid; place-items:center; border:1px solid #ccc; border-radius:50%; font-size:11px; transition:.2s; }
.host-training .ht-phase-flow > div.is-active > span { background:#111; border-color:#111; color:#fff; }
.host-training .ht-phase-flow strong { font-weight:600; font-size:15px; }
.host-training .ht-phase-flow small { grid-column:2; color:#888; font-size:11px; }
.host-training .ht-role-scene > div { padding:24px 0; }
.host-training .ht-role-scene > div + div { border-top:1px solid #ddd; }
.host-training .ht-role-scene p { color:#777; font-size:13px; margin:10px 0 0; }
.host-training .ht-offer-card { padding:26px; background:#fff; border:1px solid #e3e3e0; border-radius:17px; }
.host-training .ht-offer-card h2 { margin:18px 0 5px; font-size:27px; }
.host-training .ht-example-label { display:block; font-size:10px; color:#999; margin-bottom:22px; }
.host-training .ht-offer-card > div { display:flex; align-items:center; gap:12px; padding:12px 0; border-top:1px solid #eee; font-size:12px; color:#777; }
.host-training .ht-offer-bottom { display:flex; justify-content:space-between; gap:8px; align-items:center; margin-top:23px; font-size:10px; line-height:1.4; }
.host-training .ht-offer-bottom svg { flex-shrink:0; color:#aaa; }
.host-training .ht-guide { background:#fff; padding:26px; border:1px solid #e1e1dd; box-shadow:0 12px 25px #00000005; }
.host-training .ht-guide header { padding-bottom:14px; border-bottom:1px solid #ddd; }
.host-training .ht-guide h2 { font-size:25px; }
.host-training .ht-guide header p { margin:0; color:#888; font-size:12px; }
.host-training .ht-agenda > div { display:grid; grid-template-columns:40px 1fr; gap:18px; padding:12px 0; border-bottom:1px solid #eee; }
.host-training .ht-agenda span { color:#aaa; font-size:11px; }
.host-training .ht-agenda strong { font-size:12px; font-weight:550; }
.host-training .ht-guide footer { margin-top:17px; font-size:9px; color:#888; }
.host-training .ht-timing > div { padding:28px 0; }
.host-training .ht-timing > div + div { border-top:1px solid #ddd; }
.host-training .ht-timing strong { display:block; font-size:55px; letter-spacing:-.05em; font-weight:600; margin-top:15px; }
.host-training .ht-timing p { color:#777; font-size:13px; margin:7px 0; }
.host-training .ht-practice-caption { display:flex; justify-content:space-between; font-size:9px; color:#999; margin-bottom:18px; }
.host-training .ht-practice-caption > span { font-weight:700; letter-spacing:.06em; }
.host-training .ht-phone { max-width:300px; margin:0 auto; min-height:360px; border:6px solid #171717; border-radius:27px; background:#fff; overflow:hidden; box-shadow:0 17px 32px #0000000c; }
.host-training .ht-phone-top { display:flex; align-items:center; justify-content:space-between; padding:18px 18px 12px; border-bottom:1px solid #eee; }
.host-training .ht-phone-top > span { font-size:22px; font-weight:800; letter-spacing:-.07em; }
.host-training .ht-phone-top > small { font-size:10px; color:#777; }
.host-training .ht-phone-content { padding:23px 18px; }
.host-training .ht-phone-content > svg { margin-bottom:22px; color:#777; }
.host-training .ht-phone-content h3 { font-size:17px; margin-bottom:8px; }
.host-training .ht-phone-content > p { color:#888; font-size:11px; }
.host-training .ht-phone-content .ht-button { width:100%; font-size:11px; padding:10px 12px; min-height:40px; }
.host-training .ht-phone-details { display:flex; align-items:center; gap:8px; margin:28px 0; font-size:10px; color:#777; }
.host-training .ht-phone-tabs { display:flex; gap:6px; margin:20px 0; }
.host-training .ht-phone-tabs > button { border:1px solid #ddd; background:white; padding:7px 9px; border-radius:99px; font-size:9px; flex:1; }
.host-training .ht-phone-tabs > button.is-selected { color:#fff; background:#111; border-color:#111; }
.host-training .ht-message { padding:14px; background:#f4f4f3; border-radius:14px; margin-bottom:20px; }
.host-training .ht-message small { color:#888; font-size:9px; }
.host-training .ht-message p { font-size:11px; margin:7px 0 0; line-height:1.5; }
.host-training .ht-phone-hint { font-size:11px; line-height:1.5; }
.host-training .ht-portal-practice > .ht-text-button { display:block; margin:13px auto 0; font-size:10px; }
.host-training .ht-avatar { border:1px solid #ddd; border-radius:50%; width:36px; height:36px; display:grid; place-items:center; background:#fff; font-size:13px; }
.host-training .ht-guest { display:flex; align-items:center; gap:8px; padding:16px 0; }
.host-training .ht-guest strong, .host-training .ht-guest small { display:block; font-size:11px; }
.host-training .ht-guest small { margin-top:4px; font-size:9px; color:#888; }
.host-training .ht-guest > button { margin-left:auto; border:1px solid #ddd; background:#fff; font-size:9px; padding:7px 9px; border-radius:9px; }
.host-training .ht-chat-example { display:flex; gap:14px; padding:20px 16px; border:1px solid #ddd; border-radius:14px; margin-top:20px; }
.host-training .ht-chat-example > svg { flex-shrink:0; color:#777; }
.host-training .ht-chat-example strong { font-size:12px; }
.host-training .ht-chat-example p { font-size:11px; color:#777; margin:7px 0 0; }
.host-training .ht-conversation > .ht-caption { margin-bottom:25px; }
.host-training .ht-speech { max-width:90%; padding:18px; background:#111; border-radius:19px 19px 4px 19px; color:#fff; margin:15px 0 15px auto; font-size:14px; line-height:1.5; }
.host-training .ht-speech.is-other { margin-right:auto; margin-left:0; background:#fff; color:#111; border:1px solid #ddd; border-radius:19px 19px 19px 4px; }
.host-training .ht-introduction { display:grid; grid-template-columns:1fr 1fr; gap:20px; }
.host-training .ht-interest { text-align:center; padding:25px 12px; border:1px solid #ddd; border-radius:15px; background:#fff; }
.host-training .ht-interest .ht-avatar { margin:0 auto 14px; width:45px; height:45px; }
.host-training .ht-interest strong { font-size:14px; }
.host-training .ht-interest p { font-size:11px; color:#777; margin-bottom:0; }
.host-training .ht-intro-quote { grid-column:1/-1; font-size:21px; line-height:1.5; letter-spacing:-.02em; padding:18px 0 0; }
.host-training .ht-introduction > .ht-note { grid-column:1/-1; margin-top:0; }
.host-training .ht-safety h2 { margin:18px 0; font-size:35px; }
.host-training .ht-expectations > div { display:grid; grid-template-columns:28px 1fr; gap:5px 17px; padding:20px 0; }
.host-training .ht-expectations > div + div { border-top:1px solid #ddd; }
.host-training .ht-expectations span { grid-row:span 2; font-size:11px; color:#999; padding-top:4px; }
.host-training .ht-expectations p { margin:3px 0 0; font-size:12px; color:#777; }
.host-training .ht-pay-main > strong { display:block; margin-top:15px; font-size:80px; font-weight:600; line-height:1; letter-spacing:-.07em; }
.host-training .ht-pay-main p { color:#777; margin:10px 0 20px; font-size:14px; }
.host-training .ht-pay-main small { display:block; font-size:12px; color:#777; line-height:1.6; }
.host-training .ht-pay-zelle { display:inline-block; margin-top:20px; padding:8px 13px; background:#fff; border:1px solid #ddd; border-radius:99px; font-size:10px; }
.host-training .ht-pay-stipend { margin-top:30px; padding-top:24px; border-top:1px solid #ddd; }
.host-training .ht-pay-stipend p { color:#777; font-size:13px; }
.host-training .ht-quiz-facts { display:grid; grid-template-columns:1fr 1fr; gap:36px 20px; }
.host-training .ht-quiz-facts strong { display:block; font-size:56px; font-weight:600; letter-spacing:-.05em; }
.host-training .ht-quiz-facts span { color:#777; font-size:13px; }
.host-training .ht-quiz-facts > p { grid-column:1/-1; margin-top:0; }
.host-training .ht-knowledge-check { width:100%; max-width:760px; margin:0 auto; padding:30px 0; }
.host-training .ht-knowledge-check h1 { font-size:clamp(30px,3.5vw,44px); }
.host-training .ht-choices { display:grid; gap:10px; }
.host-training .ht-choices button { width:100%; display:flex; align-items:center; gap:15px; padding:17px; border:1px solid #ddd; background:#fff; border-radius:13px; text-align:left; font-size:14px; line-height:1.5; }
.host-training .ht-choices button > span { border-radius:8px; background:#f4f4f4; min-width:32px; height:32px; display:grid; place-items:center; font-size:11px; font-weight:700; }
.host-training .ht-choices button.is-correct { border-color:#16855b; background:#edf8f3; }
.host-training .ht-choices button.is-wrong { border-color:#b33a34; background:#fff3f2; }
.host-training .ht-feedback { margin-top:15px; border-radius:12px; background:#fff3f2; color:#9a302b; font-size:13px; padding:16px; }
.host-training .ht-feedback.is-correct { background:#edf8f3; color:#116846; }
.host-training .ht-feedback p { margin:5px 0 0; }
.host-training .ht-dialog { width:min(430px,calc(100% - 40px)); padding:28px; border:1px solid #ddd; border-radius:18px; background:#fff; color:#111; font:inherit; box-shadow:0 24px 70px #00000015; }
.host-training .ht-dialog::backdrop { background:#ffffffb8; backdrop-filter:blur(6px); }
.host-training .ht-dialog h2 { font-size:23px; font-weight:600; margin:0; }
.host-training .ht-dialog > p { color:#74746f; font-size:13px; line-height:1.6; }
.host-training .ht-dialog .ht-actions { justify-content:flex-end; gap:10px; }
.host-training .ht-paper.is-quiz {max-width:980px;}
.host-training .is-quiz .ht-header {height:72px;grid-template-columns:1fr minmax(280px,430px) 1fr;}
.host-training .ht-quiz {flex:1 1 auto;width:100%;max-width:700px;margin:0 auto;padding:28px 0;display:flex;flex-direction:column;justify-content:center;}
.host-training .ht-quiz.is-reviewing {justify-content:flex-start;}
.host-training .ht-final-review-list {list-style:none;display:grid;margin:26px 0 0;padding:0;border-top:1px solid #e8e8e8;}
.host-training .ht-final-review-item {display:grid;grid-template-columns:28px minmax(0,1fr) auto;align-items:start;gap:14px;padding:22px 0;border-bottom:1px solid #e8e8e8;}
.host-training .ht-final-review-number {color:#999;font-size:12px;line-height:24px;font-variant-numeric:tabular-nums;}
.host-training .ht-final-review-copy {min-width:0;}
.host-training .ht-final-review-question {margin:0;color:#111;font-size:16px;font-weight:600;line-height:1.5;letter-spacing:-.015em;overflow-wrap:anywhere;}
.host-training .ht-final-review-answer {display:flex;align-items:baseline;gap:8px;margin-top:9px;color:#656565;}
.host-training .ht-final-review-letter {flex:none;font-size:13px;font-weight:600;}
.host-training .ht-final-review-answer p {margin:0;font-size:14px;font-weight:400;line-height:1.6;overflow-wrap:anywhere;}
.host-training .ht-final-review-edit {min-height:36px;margin-top:-6px;padding:6px 10px;border:1px solid transparent;border-radius:6px;background:transparent;color:#555;font:inherit;font-size:12px;font-weight:600;cursor:pointer;}
.host-training .ht-final-review-edit:not(:disabled):hover {background:#f3f3f3;color:#111;}
.host-training .ht-final-review-edit:focus-visible {outline:2px solid #111;outline-offset:2px;}
.host-training .ht-final-review-edit:disabled {opacity:.5;cursor:default;}
.host-training .ht-result { flex:1; padding:70px 0; margin:auto; width:100%; max-width:700px; }
.host-training .ht-result h1 { font-size:clamp(35px,4vw,55px); }
.host-training .ht-result-score { margin:38px 0 25px; }
.host-training .ht-result-score strong { display:block; font-size:85px; font-weight:600; letter-spacing:-.07em; line-height:1; }
.host-training .ht-result-score span { display:block; color:#999; font-size:11px; margin-top:15px; }
.host-training .ht-result > p { color:#777; font-size:15px; max-width:400px; }
.host-training .ht-result-stats { display:flex; gap:25px; font-size:12px; color:#999; padding:22px 0; margin-top:25px; border-block:1px solid #eee; }
@keyframes ht-enter { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
@media(max-width:950px) {
  .host-training .ht-paper { width:calc(100% - 24px); padding-inline:25px; }
  .host-training .ht-header { grid-template-columns:1fr 1fr; gap:20px; padding-bottom:15px; }
  .host-training .ht-progress { grid-column:1/-1; grid-row:2; width:min(100%,360px); margin:auto; }
  .host-training .ht-module-name { grid-column:2; grid-row:1; font-size:10px; }
  .host-training .ht-lesson-layout { gap:5%; padding-inline:0; }
  .host-training .ht-lesson-visual { padding:23px; }
  .host-training .ht-people { gap:8px; }
  .host-training .ht-people span { width:43px; height:43px; font-size:16px; }
  .host-training .ht-connection-scene h2 { font-size:22px; }
}
@media(max-width:700px) {
  .host-training .ht-paper { width:100%; padding:18px 22px; }
  .host-training .ht-logo { width:78px; }
  .host-training .ht-header { min-height:55px; gap:22px 10px; }
  .host-training .ht-module-name { font-size:9px; }
  .host-training .ht-stage { min-height:0; padding:32px 0; }
  .host-training .ht-lesson-layout { grid-template-columns:1fr; gap:28px; }
  .host-training .ht-lesson-copy h1 { max-width:550px; font-size:38px; }
  .host-training .ht-summary p { font-size:16px; }
  .host-training .ht-transcript { margin-top:18px; }
  .host-training .ht-lesson-visual { min-height:330px; padding:26px; }
  .host-training .ht-intro { grid-template-columns:1fr; padding:40px 0; gap:35px; }
  .host-training .ht-intro h1 { font-size:38px; }
  .host-training .ht-intro-roadmap { padding:26px; }
  .host-training .ht-access { padding:110px 0; }
  .host-training .ht-access-orbit span { font-size:9px; padding:10px; }
  .host-training .ht-access-orbit span:nth-child(1) { left:5%; top:3%; }
  .host-training .ht-access-orbit span:nth-child(2) { right:2%; top:9%; }
  .host-training .ht-access-orbit span:nth-child(3) { bottom:2%; right:10%; }
  .host-training .ht-controls { display:grid; grid-template-columns:1fr; gap:16px; padding:12px 0; }
  .host-training .ht-controls > .ht-button { width:100%; }
  .host-training .ht-control-left { justify-content:space-between; gap:6px; }
  .host-training .ht-audio-note { flex-wrap:wrap; }
  .host-training .ht-quiz { padding:20px 0; }
  .host-training .is-quiz .ht-header {grid-template-columns:auto minmax(120px,1fr) auto;gap:10px;}
  .host-training .ht-final-review-item {gap:10px;}
  .host-training .ht-result { padding:45px 0; }
  .host-training .ht-result-stats { gap:20px; font-size:11px; }
  .host-training .ht-dialog .ht-actions { flex-direction:column-reverse; }
  .host-training .ht-dialog .ht-button { width:100%; }
  .host-training .ht-offer-bottom { font-size:9px; }
}
@media(prefers-reduced-motion:reduce) {
  .host-training *, .host-training *::before, .host-training *::after { animation:none!important; transition:none!important; scroll-behavior:auto!important; }
}

/* The access and overview styles below are copied from the supplied referral
   training reference, scoped to this component. Do not wrap them in ht-paper. */
.host-training .training-logo { display:block; width:72px; height:auto; }
.host-training .training-button { min-height:44px; padding:0 20px; border:1px solid #111111; border-radius:8px; background:#111111; color:white; font-size:13px; font-weight:760; cursor:pointer; }
.host-training .training-button.is-secondary { border-color:#dddddd; background:white; color:#111111; }
.host-training .training-button.is-wide { width:100%; margin-top:18px; }
.host-training .training-button:disabled { opacity:.4; cursor:not-allowed; }
  /* Ambient onboarding-style access screen */
  .host-training .access-ambient {
    position: relative;
    min-height: 100vh;
    overflow: hidden;
    display: grid;
    grid-template-rows: auto 1fr auto;
    padding: 30px 44px 24px;
    background:
      radial-gradient(circle at 50% 47%, rgba(246,246,244,.96) 0, rgba(251,251,250,.76) 27%, rgba(255,255,255,0) 55%),
      #ffffff;
  }

  .host-training .access-ambient-header {
    position: relative;
    z-index: 6;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 56px;
  }

  .host-training .access-ambient-header .training-logo {
    width: 92px;
  }

  .host-training .access-module-name {
    color: #111111;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: .055em;
    white-space: nowrap;
  }

  .host-training .access-ambient-content {
    position: relative;
    z-index: 5;
    align-self: center;
    justify-self: center;
    width: min(100%, 620px);
    margin-top: 8px;
    text-align: center;
  }

  .host-training .access-ambient-content h1 {
    margin: 0;
    color: #171717;
    font-size: clamp(30px, 3vw, 38px);
    font-weight: 600;
    line-height: 1.08;
    letter-spacing: -.035em;
  }

  .host-training .access-ambient-content form {
    margin-top: 24px;
  }

  .host-training .access-prompt-input {
    border-bottom: 1px solid #e3e3df;
    min-height: 106px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
  }

  .host-training .access-prompt-input input {
    width: min(100%, 520px);
    height: auto;
    min-width: 0;
    padding: 0;
    border: 0;
    border-radius: 0;
    outline: none;
    background: transparent;
    box-shadow: none;
    color: #171717;
    caret-color: #5b8cff;
    font-size: clamp(48px, 5.7vw, 70px);
    font-weight: 300;
    line-height: 1;
    letter-spacing: -.05em;
    text-align: center;
  }

  .host-training .access-prompt-input input::placeholder {
    color: #d7d7dc;
  }

  .host-training .access-prompt-input input:focus {
    border: 0;
    box-shadow: none;
  }

  .host-training .access-ambient-content .training-button.is-wide {
    width: auto;
    min-width: 184px;
    min-height: 52px;
    margin-top: 24px;
    padding: 0 30px;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 650;
  }

  .host-training .access-form-note {
    max-width: 520px;
    margin: 24px auto 0;
    color: #858580;
    font-size: 11px;
    line-height: 1.6;
  }

  .host-training .access-ambient-stage {
    position: absolute;
    z-index: 1;
    inset: 92px 0 90px;
    pointer-events: none;
  }

  .host-training .ambient-card,
  .host-training .ambient-float {
    position: absolute;
    border: 1px solid rgba(17,17,17,.045);
    background: rgba(250,250,249,.78);
    box-shadow: 0 10px 35px rgba(0,0,0,.018);
    backdrop-filter: blur(5px);
  }

  .host-training .ambient-card {
    border-radius: 18px;
  }

  .host-training .ambient-dashboard {
    top: 10%;
    left: 50%;
    width: 290px;
    padding: 16px;
    transform: translateX(-50%);
    opacity: .48;
    animation: ambient-dashboard-drift 8s ease-in-out infinite;
  }

  .host-training .ambient-dashboard-top,
  .host-training .ambient-dashboard-row {
    display: flex;
    align-items: center;
  }

  .host-training .ambient-dashboard-top {
    gap: 9px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(17,17,17,.035);
  }

  .host-training .ambient-dashboard-row {
    gap: 10px;
    padding-top: 12px;
  }

  .host-training .ambient-dot {
    width: 10px;
    height: 10px;
    border-radius: 3px;
    background: #d9d9d5;
  }

  .host-training .ambient-line {
    width: 88px;
    height: 8px;
    border-radius: 999px;
    background: #e4e4e0;
  }

  .host-training .ambient-line-long {
    width: 126px;
  }

  .host-training .ambient-line-short {
    width: 64px;
  }

  .host-training .ambient-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #deded9;
  }

  .host-training .ambient-pill {
    width: 54px;
    height: 18px;
    margin-left: auto;
    border-radius: 999px;
    background: #ecece8;
  }

  .host-training .ambient-pill-dark {
    background: #ddddda;
  }

  .host-training .ambient-float {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 14px;
    color: #6e6e69;
    opacity: .64;
  }

  .host-training .ambient-float strong,
  .host-training .ambient-float small {
    display: block;
  }

  .host-training .ambient-float strong {
    color: #5f5f5a;
    font-size: 10px;
    font-weight: 650;
  }

  .host-training .ambient-float small {
    margin-top: 2px;
    color: #969691;
    font-size: 8px;
  }

  .host-training .ambient-profile {
    left: 14%;
    top: 39%;
    animation: ambient-drift-one 7.5s ease-in-out infinite;
  }

  .host-training .ambient-profile-avatar {
    width: 28px;
    height: 28px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: #e8e8e4;
    color: #8a8a85;
    font-size: 9px;
    font-weight: 700;
  }

  .host-training .ambient-message {
    right: 12%;
    top: 35%;
    max-width: 235px;
    animation: ambient-drift-two 9s ease-in-out infinite;
  }

  .host-training .ambient-message p {
    margin: 0;
    color: #777772;
    font-size: 9px;
    line-height: 1.35;
  }

  .host-training .ambient-message-dot {
    width: 8px;
    height: 8px;
    flex: 0 0 auto;
    border-radius: 50%;
    background: #d8d8d4;
  }

  .host-training .ambient-qualified {
    right: 17%;
    bottom: 18%;
    animation: ambient-drift-three 8.5s ease-in-out infinite;
  }

  .host-training .ambient-qualified > span {
    width: 28px;
    height: 28px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: #edede9;
    color: #767671;
    font-size: 11px;
    font-weight: 700;
  }

  .host-training .ambient-step {
    left: 20%;
    bottom: 20%;
    animation: ambient-drift-four 10s ease-in-out infinite;
  }

  .host-training .ambient-step > span {
    color: #a1a19c;
    font-size: 8px;
    font-weight: 700;
  }

  .host-training .ambient-step strong {
    font-size: 9px;
  }

  .host-training .access-ambient-footer {
    position: relative;
    z-index: 6;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 36px;
  }

  .host-training .access-legal {
    color: #a0a09b;
    font-size: 9px;
    font-weight: 500;
  }

  .host-training .access-centered-socials {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
  }

  .host-training .access-centered-socials a {
    color: #000;
    font-size: 1.28rem;
    line-height: 1;
    transition: color 0.2s ease;
  }

  @keyframes ambient-dashboard-drift {
    0%, 100% { transform: translate(-50%, 0); }
    50% { transform: translate(-50%, -7px); }
  }

  @keyframes ambient-drift-one {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(6px, -8px); }
  }

  @keyframes ambient-drift-two {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(-7px, 6px); }
  }

  @keyframes ambient-drift-three {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(5px, -6px); }
  }

  @keyframes ambient-drift-four {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(-5px, -7px); }
  }

  @media (max-width: 1000px) {
    .host-training .ambient-profile { left: 6%; }
    .host-training .ambient-message { right: 5%; }
    .host-training .ambient-qualified { right: 7%; }
    .host-training .ambient-step { left: 8%; }
  }

  @media (max-width: 760px) {
    .host-training .access-ambient {
      padding: 24px 22px 18px;
    }

    .host-training .access-module-name {
      font-size: 10px;
    }

    .host-training .access-ambient-stage {
      opacity: .42;
    }

    .host-training .ambient-profile,
    .host-training .ambient-message,
    .host-training .ambient-qualified,
    .host-training .ambient-step {
      display: none;
    }

    .host-training .ambient-dashboard {
      top: 14%;
      width: 250px;
    }

    .host-training .access-ambient-content {
      margin-top: 0;
    }
  }

  @media (max-width: 520px) {
    .host-training .access-ambient-header .training-logo {
      width: 84px;
    }

    .host-training .access-module-name {
      font-size: 9px;
      letter-spacing: .035em;
    }

    .host-training .access-ambient-content h1 {
      font-size: 30px;
    }

    .host-training .access-prompt-input {
      min-height: 82px;
    }

    .host-training .access-prompt-input input {
      font-size: 44px;
    }

    .host-training .ambient-dashboard {
      width: 220px;
      opacity: .36;
    }

    .host-training .access-legal {
      max-width: 180px;
      line-height: 1.35;
    }
  }

  /* Open training overview screen */
  .host-training .intro-open {
    position: relative;
    min-height: 100vh;
    overflow: hidden;
    display: grid;
    grid-template-rows: auto 1fr auto;
    padding: 30px 44px 24px;
    background: #ffffff;
  }

  .host-training .intro-open-header {
    position: relative;
    z-index: 4;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 56px;
  }

  .host-training .intro-open-header .training-logo {
    width: 92px;
  }

  .host-training .intro-open-module-name {
    color: #111111;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: .055em;
    white-space: nowrap;
  }

  .host-training .intro-background-details {
    position: absolute;
    z-index: 1;
    inset: 86px 0 54px;
    pointer-events: none;
  }

  .host-training .intro-bg-card {
    position: absolute;
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 10px 12px;
    border: 1px solid rgba(17, 17, 17, .035);
    border-radius: 13px;
    background: rgba(248, 248, 246, .78);
    box-shadow: 0 10px 28px rgba(0, 0, 0, .012);
    opacity: .32;
    backdrop-filter: blur(4px);
  }

  .host-training .intro-bg-card-one {
    top: 11%;
    left: 46%;
    width: 215px;
    animation: intro-bg-float-one 10s ease-in-out infinite;
  }

  .host-training .intro-bg-card-two {
    left: 5%;
    bottom: 16%;
    width: 150px;
    opacity: .24;
    animation: intro-bg-float-two 12s ease-in-out infinite;
  }

  .host-training .intro-bg-card-three {
    right: 4%;
    bottom: 11%;
    width: 180px;
    opacity: .26;
    animation: intro-bg-float-three 11s ease-in-out infinite;
  }

  .host-training .intro-bg-avatar {
    width: 25px;
    height: 25px;
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: #deded9;
    color: #8b8b85;
    font-size: 8px;
    font-weight: 700;
  }

  .host-training .intro-bg-dot {
    width: 8px;
    height: 8px;
    flex: 0 0 auto;
    border-radius: 50%;
    background: #d6d6d1;
  }

  .host-training .intro-bg-check {
    width: 24px;
    height: 24px;
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: #e1e1dd;
    color: #8d8d87;
    font-size: 9px;
    font-weight: 700;
  }

  .host-training .intro-bg-card > div {
    display: grid;
    gap: 3px;
    min-width: 0;
  }

  .host-training .intro-bg-card strong,
  .host-training .intro-bg-card small {
    display: block;
  }

  .host-training .intro-bg-card strong {
    color: #686863;
    font-size: 9px;
    font-weight: 650;
    line-height: 1.2;
  }

  .host-training .intro-bg-card small {
    color: #969691;
    font-size: 7px;
    line-height: 1.25;
  }

  .host-training .intro-bg-outreach {
    width: 205px;
  }

  .host-training .intro-bg-outreach p {
    margin: 0;
    color: #858580;
    font-size: 8px;
    line-height: 1.35;
  }

  .host-training .intro-bg-line {
    display: block;
    height: 6px;
    border-radius: 999px;
    background: #e5e5e1;
  }

  .host-training .intro-bg-line-long {
    width: 92px;
  }

  .host-training .intro-bg-line-medium {
    width: 76px;
  }

  .host-training .intro-bg-line-short {
    width: 46px;
  }

  .host-training .intro-bg-pill {
    min-width: 38px;
    height: 16px;
    display: grid;
    place-items: center;
    margin-left: auto;
    padding: 0 7px;
    border-radius: 999px;
    background: #ebebe7;
    color: #989892;
    font-size: 6px;
    font-weight: 750;
    letter-spacing: .06em;
  }

  @keyframes intro-bg-float-one {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(5px, -6px); }
  }

  @keyframes intro-bg-float-two {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(-4px, 5px); }
  }

  @keyframes intro-bg-float-three {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(4px, -5px); }
  }

  .host-training .intro-open-main {
    position: relative;
    z-index: 3;
    align-self: center;
    width: min(100%, 1190px);
    margin: 0 auto;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(300px, .55fr);
    gap: clamp(74px, 10vw, 150px);
    align-items: center;
    padding: 56px 0 64px;
  }

  .host-training .intro-open-copy {
    max-width: 720px;
  }

  .host-training .intro-open-copy h1 {
    max-width: 620px;
    margin: 0;
    color: #171717;
    font-size: clamp(30px, 3vw, 38px);
    font-weight: 600;
    line-height: 1.08;
    letter-spacing: -.035em;
  }

  .host-training .intro-open-copy > p {
    max-width: 400px;
    margin: 26px 0 0;
    color: #686864;
    font-size: 16px;
    line-height: 1.65;
  }

  .host-training .intro-open-stats {
    display: flex;
    gap: 48px;
    margin-top: 42px;
  }

  .host-training .intro-open-stats strong,
  .host-training .intro-open-stats span {
    display: block;
  }

  .host-training .intro-open-stats strong {
    color: #171717;
    font-size: 21px;
    font-weight: 650;
    letter-spacing: -.025em;
  }

  .host-training .intro-open-stats span {
    margin-top: 4px;
    color: #989893;
    font-size: 10px;
    font-weight: 550;
  }

  .host-training .intro-account-stat {
    min-width: 0;
    max-width: 190px;
  }

  .host-training .intro-account-stat strong {
    overflow: hidden;
    max-width: 190px;
    font-size: 16px;
    line-height: 1.3;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .host-training .intro-open-actions {
    display: flex;
    align-items: center;
    gap: 22px;
    margin-top: 38px;
  }

  .host-training .intro-open-actions .training-button {
    min-width: 174px;
    min-height: 50px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 650;
  }

  .host-training .intro-status-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
  }

  .host-training .intro-status-button:disabled {
    opacity: 1;
    cursor: default;
  }

  .host-training .intro-review-content {
    padding: 0;
    border: 0;
    background: transparent;
    color: #777772;
    font-size: 11px;
    font-weight: 550;
    cursor: pointer;
  }

  .host-training .intro-review-content:hover {
    color: #111111;
  }

  .host-training .intro-change-email {
    padding: 0;
    border: 0;
    background: transparent;
    color: #8c8c87;
    font-size: 11px;
    font-weight: 550;
    cursor: pointer;
  }

  .host-training .intro-change-email:hover {
    color: #111111;
  }

  .host-training .intro-stepper {
    width: 100%;
    max-width: 500px;
    height: 390px;
    justify-self: end;
    overflow: hidden;
    -webkit-mask-image: linear-gradient(
      to bottom,
      #000 0%,
      #000 68%,
      rgba(0,0,0,.78) 79%,
      rgba(0,0,0,.35) 90%,
      transparent 100%
    );
    mask-image: linear-gradient(
      to bottom,
      #000 0%,
      #000 68%,
      rgba(0,0,0,.78) 79%,
      rgba(0,0,0,.35) 90%,
      transparent 100%
    );
  }

  .host-training .intro-stepper.is-ending {
    -webkit-mask-image: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(0,0,0,.28) 9%,
      rgba(0,0,0,.72) 20%,
      #000 32%,
      #000 100%
    );
    mask-image: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(0,0,0,.28) 9%,
      rgba(0,0,0,.72) 20%,
      #000 32%,
      #000 100%
    );
  }

  .host-training .intro-stepper-list {
    position: relative;
    padding: 10px 0 54px 150px;
    transition: transform .35s ease;
  }

  .host-training .intro-stepper-list::before {
    content: "";
    position: absolute;
    left: 158px;
    top: 18px;
    bottom: 0;
    width: 1px;
    background: #d7d7d3;
  }

.host-training .intro-stepper-item {
  position: relative;
  display: grid;
  grid-template-columns: 18px 1fr;
  gap: 14px;
  align-items: start;
  min-height: 70px;
  padding-bottom: 22px;
  opacity: 1;
}

.host-training .intro-stepper-item:not(.is-complete):not(.is-current) .intro-stepper-copy {
  opacity: .5;
}

.host-training .intro-stepper-item.is-complete .intro-stepper-copy {
  opacity: .72;
}

.host-training .intro-stepper-node {
  position: relative;
  z-index: 2;
  width: 16px;
  height: 16px;
  display: grid;
  place-items: center;
  margin-top: 1px;
  border: 1.5px solid #bdbdb8;
  border-radius: 50%;
  background: #ffffff;
  color: #ffffff;
  font-size: 8px;
  font-weight: 700;
}

  .host-training .intro-stepper-item.is-complete {
    opacity: .72;
  }

  .host-training .intro-stepper-item.is-current {
    opacity: 1;
  }

  .host-training .intro-you-are-here {
    position: absolute;
    left: -130px;
    top: -7px;
    width: max-content;
    min-width: 112px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 14px;
    border: 0;
    border-radius: 9px;
    background: #111111;
    color: #ffffff;
    font-family: "Ugly Handwriting", cursive;
    font-size: 14px;
    font-weight: 400;
    line-height: 1;
    letter-spacing: 0;
    white-space: nowrap;
    text-align: center;
    transform: rotate(-2deg);
  }

  .host-training .intro-you-are-here::before {
    content: "";
    position: absolute;
    right: -7px;
    top: 50%;
    width: 14px;
    height: 14px;
    background: #111111;
    transform: translateY(-50%) rotate(45deg);
  }

  .host-training .intro-stepper-node {
    position: relative;
    z-index: 2;
    width: 16px;
    height: 16px;
    display: grid;
    place-items: center;
    margin-top: 1px;
    border: 1.5px solid #bdbdb8;
    border-radius: 50%;
    background: #ffffff;
    color: #ffffff;
    font-size: 8px;
    font-weight: 700;
  }

  .host-training .intro-stepper-item:not(.is-complete):not(.is-current) .intro-stepper-node {
    background: #ffffff;
    border-color: #bdbdb8;
  }

  .host-training .intro-stepper-item.is-complete .intro-stepper-node {
    border-color: #111111;
    background: #111111;
  }

  .host-training .intro-stepper-item.is-current .intro-stepper-node {
    border-color: #111111;
    background: #111111;
    box-shadow: 0 0 0 5px rgba(17,17,17,.055);
  }

  .host-training .intro-stepper-copy strong,
  .host-training .intro-stepper-copy small {
    display: block;
  }

  .host-training .intro-stepper-copy strong {
    max-width: 290px;
    color: #666661;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.3;
  }

  .host-training .intro-stepper-item.is-current .intro-stepper-copy strong {
    color: #171717;
    font-weight: 650;
  }

  .host-training .intro-stepper-copy small {
    margin-top: 4px;
    color: #989893;
    font-size: 10px;
  }

  .host-training .intro-open-footer {
    position: relative;
    z-index: 4;
    min-height: 36px;
    display: flex;
    align-items: center;
    color: #a0a09b;
    font-size: 9px;
    font-weight: 500;
  }

  @media (max-width: 900px) {
    .host-training .intro-open-main {
      width: min(100%, 760px);
      grid-template-columns: 1fr;
      gap: 46px;
      padding: 48px 0 44px;
    }

    .host-training .intro-stepper {
      width: min(100%, 470px);
      height: 340px;
      justify-self: start;
    }
  }

  @media (max-width: 400px) {
    .host-training .intro-background-details {
      display: none;
    }

    .host-training .intro-open {
      padding: 24px 22px 18px;
    }

    .host-training .intro-open-header .training-logo {
      width: 84px;
    }

    .host-training .intro-open-module-name {
      font-size: 9px;
      letter-spacing: .035em;
    }

    .host-training .intro-open-main {
      padding-top: 38px;
    }

    .host-training .intro-open-copy > p {
      font-size: 14px;
    }

    .host-training .intro-open-stats {
      gap: 20px;
      justify-content: space-between;
    }

    .host-training .intro-open-actions {
      align-items: stretch;
      flex-direction: column;
    }

    .host-training .intro-review-content {
      min-height: 34px;
    }

    .host-training .intro-open-actions .training-button {
      width: 100%;
    }

    .host-training .intro-stepper {
      height: 310px;
    }

    .host-training .intro-stepper-list {
      padding-left: 126px;
    }

    .host-training .intro-stepper-list::before {
      left: 134px;
    }

    .host-training .intro-you-are-here {
      left: 2px;
      min-width: 100px;
      height: 30px;
      padding: 0 11px;
      font-size: 12px;
      white-space: nowrap;
    }

    .host-training .intro-stepper-item {
      min-height: 62px;
    }
  }

/* Host-specific text in the reference's ambient guide card. */
.host-training .ambient-dashboard-top strong { margin:0; color:#777772; font-size:9px; font-weight:650; }
.host-training .ambient-guide-label { color:#777772; font-size:8px; }
.host-training .ambient-pill { display:grid; place-items:center; color:#969691; font-size:7px; }
.host-training .intro-open-footer { justify-content:space-between; gap:20px; }
/* Keep the pointer attached to its node on narrow screens. */
@media(max-width:400px) { .host-training .intro-you-are-here { left:-118px; } }
/* Align lesson-player margins and typography with the same reference. */
.host-training .ht-header { height:72px; grid-template-columns:1fr minmax(280px,430px) 1fr; }
.host-training .ht-module-name { font-size:12px; }
.host-training .ht-stage { min-height:min(640px,calc(100vh - 210px)); padding:0; }
.host-training .ht-lesson-layout { grid-template-columns:minmax(0,1fr) minmax(400px,1.05fr); padding:42px 4%; }
.host-training .ht-lesson-copy h1 { max-width:620px; margin:13px 0 20px; line-height:1.02; }
.host-training .ht-summary p { max-width:540px; font-size:clamp(16px,1.3vw,20px); }
.host-training .ht-lesson-visual { min-height:390px; padding:34px; }
@media(max-width:900px) {
 .host-training .ht-paper { width:100%; padding:14px 17px 18px; }
 .host-training .ht-lesson-layout { grid-template-columns:1fr; gap:25px; padding:34px 0; }
 .host-training .ht-lesson-copy h1 { max-width:680px; font-size:clamp(36px,9vw,52px); }
 .host-training .ht-summary p { max-width:620px; }
 .host-training .ht-lesson-visual { min-height:320px; padding:24px; }
}
@media(max-width:520px) {
 .host-training .ht-header { height:auto; grid-template-columns:auto minmax(120px,1fr) auto; gap:10px; }
 .host-training .ht-module-name { font-size:9px; letter-spacing:.035em; }
 .host-training .ht-lesson-visual { padding:18px; border-radius:18px; }
}
@media(prefers-reduced-motion:reduce) {
 .host-training *, .host-training *::before, .host-training *::after { animation-duration:.001ms!important; transition-duration:.001ms!important; }
}
.host-training .training-header-progress {width:100%;max-width:430px;min-width:0;justify-self:center;padding-top:4px;font-family:Inter,system-ui,sans-serif;}
.host-training .training-progress-labels {display:flex;align-items:baseline;justify-content:center;gap:10px;margin-top:10px;min-width:0;}
.host-training .training-progress-title {min-width:0;color:#353535;font-size:12px;font-weight:600;line-height:1.4;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.host-training .training-progress-count {flex:none;color:#8b8b87;font-size:10px;font-weight:400;line-height:1.4;white-space:nowrap;}
.host-training .training-progress-track {height:5px;width:100%;overflow:hidden;border-radius:999px;background:#eeefed;}
.host-training .training-progress-fill {display:block;height:100%;border-radius:inherit;background:#252525;transition:width 240ms ease;}
@media(max-width:700px){.host-training .training-header-progress {grid-column:1 / -1;grid-row:2;max-width:430px;}}
@media(max-width:520px){
 .host-training .training-progress-labels {gap:9px;margin-top:9px;}
 .host-training .training-progress-title {font-size:11px;}
 .host-training .training-progress-count {font-size:10px;}
}
@media(prefers-reduced-motion:reduce){.host-training .training-progress-fill {transition:none;}}

@media(max-width:520px){
 .host-training .is-quiz.ht-paper {padding:14px 17px 18px;}
 .host-training .is-quiz .ht-logo {width:84px;}
 .host-training .is-quiz .ht-module-name {font-size:9px;letter-spacing:.035em;white-space:normal;text-align:right;}
 .host-training .ht-final-review-item {grid-template-columns:22px minmax(0,1fr) auto;gap:8px;padding:19px 0;}
 .host-training .ht-final-review-question {font-size:15px;}
 .host-training .ht-final-review-answer p {font-size:13px;}
 .host-training .ht-final-review-edit {padding-inline:7px;}
}
`;

const DESKTOP_STYLES = `
.host-training .ht-desktop-notice {--ht-notice-nav-height:calc(80px + env(safe-area-inset-top,0px));position:fixed;top:var(--ht-notice-visible-top,0px);left:0;right:0;width:100%;height:100vh;height:var(--ht-notice-visible-height,100svh);z-index:99999;display:flex;flex-direction:column;align-items:center;justify-content:flex-start;padding:0;overflow-y:auto;background:#fff;animation:ht-desktop-fade-in 220ms ease-out;}
.host-training .ht-desktop-navbar {position:absolute;top:0;left:0;width:100%;height:var(--ht-notice-nav-height);display:flex;align-items:center;justify-content:center;padding:calc(22px + env(safe-area-inset-top,0px)) 24px 22px;background:#fff;}
.host-training .ht-desktop-body {width:100%;min-height:100%;flex:1 0 auto;display:flex;flex-direction:column;align-items:center;padding:var(--ht-notice-nav-height) 0 calc(var(--ht-notice-nav-height) + 16px + env(safe-area-inset-bottom,0px) + clamp(0px,calc(var(--ht-notice-visible-height,100svh) - 560px),72px));}
.host-training .ht-desktop-card {width:min(100%,360px);flex:none;margin:auto 0;padding:0 26px 28px;display:flex;flex-direction:column;align-items:center;text-align:center;font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}
.host-training .ht-desktop-logo {width:72px;height:auto;display:block;margin:0;}
.host-training .ht-desktop-logo :is(path,circle,rect,polygon,polyline,ellipse) {fill:#050505!important;}
.host-training .ht-desktop-logo [fill="none"] {fill:none!important;}
.host-training .ht-desktop-logo * {stroke:none!important;}
.host-training .ht-desktop-illustration {display:block;width:min(208px,100%);height:auto;flex:none;overflow:visible;margin:0 0 16px;}
.host-training .ht-desktop-copy {width:100%;max-width:250px;}
.host-training .ht-desktop-notice h1 {margin:0;color:#111;font-family:inherit;font-size:1.15rem;line-height:1.05;font-weight:850;letter-spacing:-.035em;}
.host-training .ht-desktop-notice p {margin:8px 0 0;color:rgba(17,17,17,.58);font-size:.9rem;line-height:1.36;font-weight:500;letter-spacing:-.018em;}
.host-training .ht-copy-training-link {min-width:174px;min-height:44px;display:inline-flex;align-items:center;justify-content:center;margin-top:22px;padding:12px 22px;border:1px solid #111;border-radius:999px;background:#111;color:#fff;font-family:inherit;font-size:13px;font-weight:600;line-height:1.3;letter-spacing:-.01em;cursor:pointer;transition:background 120ms ease,transform 120ms ease;}
.host-training .ht-copy-training-link:active:not(:disabled) {background:#292929;transform:scale(.98);}
.host-training .ht-copy-training-link:disabled {opacity:.65;cursor:wait;}
.host-training .ht-copy-training-link:focus-visible {outline:2px solid #111;outline-offset:4px;}
.host-training .ht-back-home-link {display:inline-flex;align-items:center;justify-content:center;min-height:44px;margin-top:4px;padding:10px 4px;color:#777;font-family:inherit;font-size:12px;font-weight:500;line-height:1.4;text-decoration:none;}
.host-training .ht-back-home-link:hover {color:#111;text-decoration:underline;text-underline-offset:3px;}
.host-training .ht-back-home-link:focus-visible {outline:2px solid #111;outline-offset:2px;border-radius:4px;}
.host-training .ht-copy-link-fallback {margin-top:12px;width:100%;}
.host-training .ht-copy-link-fallback p {margin:0 0 8px;font-size:12px;line-height:1.4;font-weight:400;letter-spacing:0;}
.host-training .ht-copy-link-fallback input {display:block;width:100%;min-width:0;padding:10px 12px;border:1px solid #ddd;border-radius:8px;background:#fafafa;color:#555;font-family:inherit;font-size:12px;line-height:1.4;-webkit-user-select:text;user-select:text;}
.host-training .ht-notice-window-size {animation:ht-notice-browser-widen 6.4s cubic-bezier(.45,0,.2,1) infinite;}
.host-training .ht-notice-window-shadow {filter:drop-shadow(0 4px 5px #17202d0d);}
.host-training .ht-notice-resize-cursor {transform:translate(176px,91px);animation:ht-notice-cursor-drag 6.4s cubic-bezier(.45,0,.2,1) infinite;}
@keyframes ht-notice-browser-widen {0%,18%{width:134px}52%,84%{width:196px}100%{width:134px}}
@keyframes ht-notice-cursor-drag {0%,8%{transform:translate(176px,91px);opacity:0}18%{transform:translate(176px,91px);opacity:.75}52%,63%{transform:translate(238px,91px);opacity:.75}76%,84%{transform:translate(238px,91px);opacity:0}100%{transform:translate(176px,91px);opacity:0}}
@keyframes ht-desktop-fade-in {from{opacity:0}to{opacity:1}}
@media(max-height:480px) {.host-training .ht-desktop-notice{--ht-notice-nav-height:calc(72px + env(safe-area-inset-top,0px))}.host-training .ht-desktop-navbar{padding:calc(18px + env(safe-area-inset-top,0px)) 24px 18px}.host-training .ht-desktop-body{padding-top:var(--ht-notice-nav-height);padding-bottom:calc(var(--ht-notice-nav-height) + env(safe-area-inset-bottom,0px))}.host-training .ht-desktop-card{padding:0 24px 16px}.host-training .ht-desktop-illustration{width:160px;margin-bottom:14px}}
@media(prefers-reduced-motion:reduce) {.host-training .ht-desktop-notice{animation:none}.host-training .ht-notice-window-size{animation:none;width:196px}.host-training .ht-notice-resize-cursor{animation:none;opacity:0}}
`;
